import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { endpoint } from './core.service';

// Interface definitions matching Django API responses
export interface MetricCard {
  amount: string;
  currency: string;
  change_percentage: number;
  period: string;
}

export interface TransactionCountCard {
  count: number;
  change_percentage: number;
  period: string;
}

export interface DashboardSummary {
  total_revenue: MetricCard;
  total_transactions: TransactionCountCard;
  estimated_fees: MetricCard;
  total_refunds: MetricCard;
  generated_at: string;
}

// DeliverFasta Dashboard Interfaces (based on REPORTS_SPECIFICATION.md)
export interface DashboardSummaryStatistics {
  total_revenue: string;
  total_orders: number;
  completed_orders: number;
  pending_orders: number;
  total_hubs: number;
  total_products: number;
  total_retailers: number;
  total_drivers: number;
  total_commissions: string;
  total_bales_sold: number;
  period: string;
  generated_at: string;
}

/** GenWatt home dashboard — GET /api/dashboard/summary/ */
export interface LogisticsDashboardSummary {
  trips: {
    total: number;
    open: number;
    completed: number;
    in_transit: number;
  };
  fuel_orders: {
    total: number;
    pending: number;
    completed: number;
    cancelled: number;
  };
  staff: {
    total: number;
    active: number;
    on_leave: number;
    departments: number;
  };
  expenses: {
    total: number;
    draft: number;
    pending: number;
    paid: number;
  };
}

export type StationFuelType = 'diesel' | 'petrol' | 'kerosene';

export interface StationFuelPrice {
  id?: string;
  fuel_type: StationFuelType;
  fuel_type_display?: string;
  price_per_litre: string | number;
  is_active?: boolean;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface StationFuelPriceWrite {
  fuel_type: StationFuelType;
  price_per_litre: string | number;
  is_active?: boolean;
  notes?: string;
}

export interface Station {
  id: string;
  company?: string;
  company_name?: string;
  code: string;
  name: string;
  brand?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  county?: string;
  postal_code?: string;
  country?: string;
  lat?: string | number | null;
  lng?: string | number | null;
  allows_credit?: boolean;
  credit_terms?: { id: string; name?: string; code?: string; days?: number } | null;
  credit_terms_id?: string | null;
  opening_outstanding?: string | number | null;
  fuel_prices?: StationFuelPrice[];
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StationListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Station[];
}

export interface CreateStationRequest {
  company_id: string;
  name: string;
  brand?: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  county?: string;
  postal_code?: string;
  country?: string;
  lat?: string | number | null;
  lng?: string | number | null;
  allows_credit?: boolean;
  credit_terms_id?: string | null;
  opening_outstanding?: string | number | null;
  notes?: string;
  is_active?: boolean;
  fuel_prices_write?: StationFuelPriceWrite[];
}

export type UpdateStationRequest = Partial<CreateStationRequest>;

export interface FuelOrderListItem {
  id: string;
  display_id: string;
  station_name: string;
  vehicles_display?: string;
  total_litres: string;
  fuel_types_display: string;
  status_name: string;
  status_code: string;
  placed_by_name?: string | null;
  filled_at?: string | null;
  created_at: string;
}

export interface FuelOrderLineWrite {
  vehicle_id: string;
  trip_id?: string;
  driver_id?: string;
  fuel_type: 'diesel' | 'petrol' | 'kerosene';
  litres: string | number;
  unit_price?: string | number;
  odometer_reading?: string | number;
  notes?: string;
}

export interface CreateFuelOrderRequest {
  station_id: string;
  payment_terms?: 'credit' | 'cash' | 'mpesa';
  invoice_number?: string;
  invoice_date?: string;
  receipt_number?: string;
  notes?: string;
  lines_write: FuelOrderLineWrite[];
}

export interface UpdateFuelOrderRequest {
  station_id?: string;
  payment_terms?: 'credit' | 'cash' | 'mpesa';
  invoice_number?: string;
  invoice_date?: string;
  receipt_number?: string;
  notes?: string;
  lines_write?: FuelOrderLineWrite[];
}

export interface FuelOrderLine {
  id?: string;
  vehicle?: string;
  vehicle_id?: string;
  vehicle_registration?: string;
  vehicle_fleet_number?: string | null;
  trip_id?: string | null;
  trip_display_id?: string | null;
  driver_id?: string | null;
  driver_name?: string | null;
  fuel_type: string;
  fuel_type_display?: string;
  litres: string;
  unit_price?: string | null;
  line_total?: string | null;
  subtotal?: string | null;
  odometer_reading?: string | number | null;
  notes?: string | null;
}

export interface FuelOrderStationPayment {
  id: string;
  amount: string;
  invoice_number?: string | null;
  payment_method?: { id?: string; code?: string; name?: string } | null;
  reference?: string | null;
  notes?: string | null;
  paid_by_name?: string | null;
  paid_at?: string | null;
  created_at?: string;
}

export interface FuelOrderDetail extends FuelOrderListItem {
  /** Detail API returns the station name as a string (`source='station.name'`). */
  station?: string | Station;
  station_code?: string | null;
  station_id?: string;
  company_name?: string | null;
  payment_terms?: string;
  payment_terms_display?: string;
  invoice_number?: string | null;
  invoice_date?: string | null;
  receipt_number?: string | null;
  notes?: string | null;
  total_amount?: string | null;
  amount_paid?: string | null;
  balance_outstanding?: string | null;
  payment_status?: string | null;
  payment_status_display?: string | null;
  payment_due_date?: string | null;
  is_payment_overdue?: boolean;
  status?: { id?: string; name?: string; code?: string } | string;
  lines?: FuelOrderLine[];
  station_payments?: FuelOrderStationPayment[];
  pdf_url?: string | null;
  pdf_file?: string | null;
  updated_at?: string;
}

export interface FuelOrderListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: FuelOrderListItem[];
  summary?: FuelOrdersSummary;
}

export interface FuelOrdersSummary {
  total: number;
  pending: number;
  completed: number;
  cancelled: number;
  total_litres?: string;
  total_amount?: string;
}

// ─── Parking: configs, yards, vehicle owners, sessions and bills ────────────

/** Parking list endpoints may return a DRF page or a bare array. */
export interface ParkingListResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ParkingConfigCreditTerms {
  id: string;
  code?: string;
  name?: string;
  days?: number;
}

export interface ParkingConfig {
  id: string;
  company_name?: string;
  daily_rate: string;
  grace_period_minutes: number;
  invoice_day_of_month: number;
  credit_terms?: ParkingConfigCreditTerms | null;
  credit_terms_id?: string | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ParkingConfigWrite {
  daily_rate: string;
  grace_period_minutes?: number;
  invoice_day_of_month?: number;
  credit_terms_id?: string | null;
  notes?: string;
  is_active?: boolean;
}

export interface ParkingYard {
  id: string;
  company_name?: string;
  code: string;
  name: string;
  street?: string | null;
  city?: string | null;
  county?: string | null;
  capacity?: number | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ParkingYardWrite {
  name: string;
  street?: string;
  city?: string;
  county?: string;
  capacity?: number | null;
  notes?: string;
  is_active?: boolean;
}

export interface VehicleOwner {
  id: string;
  company_name?: string;
  code: string;
  name: string;
  phone?: string | null;
  vehicle_count?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface VehicleOwnerWrite {
  name: string;
  phone?: string;
  is_active?: boolean;
}

export interface OwnerVehicle {
  id: string;
  company_name?: string;
  owner?: string;
  owner_id?: string;
  owner_name?: string;
  owner_code?: string;
  registration_number: string;
  vehicle?: string | null;
  vehicle_id?: string | null;
  make_model?: string | null;
  color?: string | null;
  notes?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface OwnerVehicleWrite {
  owner_id: string;
  registration_number: string;
  vehicle_id?: string | null;
  make_model?: string;
  color?: string;
  notes?: string;
  is_active?: boolean;
}

export type ParkingSessionStatus = 'active' | 'completed' | 'billed';

export interface ParkingSession {
  id: string;
  display_id: string;
  session_number?: number;
  company_name?: string;
  owner_vehicle?: string;
  owner_vehicle_id?: string;
  registration_number?: string;
  make_model?: string | null;
  owner?: string;
  owner_id?: string;
  owner_name?: string;
  owner_code?: string;
  yard?: string;
  yard_id?: string;
  yard_name?: string;
  yard_code?: string;
  entry_at: string;
  exit_at?: string | null;
  entry_notes?: string | null;
  exit_notes?: string | null;
  periods_24h?: number | null;
  fee_amount?: string | null;
  status: ParkingSessionStatus;
  status_display?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ParkingSessionEntryRequest {
  owner_vehicle_id: string;
  yard_id: string;
  entry_at?: string;
  entry_notes?: string;
}

export interface ParkingSessionExitRequest {
  exit_at?: string;
  exit_notes?: string;
}

export type ParkingBillStatus = 'draft' | 'issued' | 'paid' | 'cancelled';

export interface ParkingBillLine {
  id?: string;
  session?: string;
  session_display_id?: string;
  registration_number?: string;
  description?: string;
  periods_24h?: number;
  amount?: string;
}

export interface ParkingBillPayment {
  id: string;
  bill?: string;
  bill_display_id?: string;
  amount: string;
  payment_method?: string;
  payment_method_id?: string;
  payment_method_name?: string;
  reference?: string | null;
  notes?: string | null;
  paid_at?: string;
  created_at?: string;
}

export interface ParkingBill {
  id: string;
  display_id: string;
  bill_number?: number;
  company_name?: string;
  owner?: string;
  owner_id?: string;
  owner_name?: string;
  owner_code?: string;
  period_start: string;
  period_end: string;
  total_amount: string;
  amount_paid?: string;
  balance_outstanding?: string;
  payment_status?: string;
  payment_status_display?: string;
  due_date?: string | null;
  status: ParkingBillStatus;
  status_display?: string;
  issued_at?: string | null;
  notes?: string | null;
  lines?: ParkingBillLine[];
  payments?: ParkingBillPayment[];
  created_at?: string;
  updated_at?: string;
}

export interface GenerateParkingBillsRequest {
  year: number;
  month: number;
  issue?: boolean;
}

export interface GenerateParkingBillForOwnerRequest extends GenerateParkingBillsRequest {
  owner_id: string;
}

export interface GenerateParkingBillsResponse {
  created?: number;
  issued?: number;
  skipped?: number;
  detail?: string;
  bills?: ParkingBill[];
}

export interface ParkingBillPayRequest {
  amount: string;
  payment_method_id: string;
  reference?: string;
  notes?: string;
  paid_at?: string;
}

// ─── Finance: expense categories, expenses, petty cash ──────────────────────

export type ExpensePaymentSource = 'cash' | 'mpesa' | 'bank' | 'petty_cash';
export type ExpenseStatus = 'draft' | 'submitted' | 'approved' | 'paid' | 'void';
export type PettyCashTransactionType = 'replenish' | 'expense' | 'adjustment';

export interface ExpenseCategory {
  id: string;
  code?: string;
  name: string;
  description?: string;
  system_key?: string | null;
  is_vehicle_expense: boolean;
  is_active: boolean;
  company_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ExpenseCategoryListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: ExpenseCategory[];
}

export interface CreateExpenseCategoryRequest {
  name: string;
  description?: string;
  is_vehicle_expense?: boolean;
  is_active?: boolean;
}

export interface PettyCashFund {
  id: string;
  code?: string;
  name: string;
  float_limit?: string;
  balance?: string;
  notes?: string;
  is_active: boolean;
  branch?: string | null;
  branch_id?: string | null;
  branch_name?: string | null;
  custodian?: string | null;
  custodian_id?: string | null;
  custodian_name?: string | null;
  company_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PettyCashFundListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: PettyCashFund[];
}

export interface CreatePettyCashFundRequest {
  name: string;
  float_limit?: string;
  branch_id?: string;
  custodian_id?: string;
  notes?: string;
  is_active?: boolean;
}

export interface ReplenishPettyCashFundRequest {
  amount: string;
  reference?: string;
  notes?: string;
}

export interface PettyCashTransaction {
  id: string;
  fund?: string;
  fund_code?: string;
  fund_name?: string;
  transaction_type: PettyCashTransactionType;
  transaction_type_display?: string;
  amount: string;
  balance_after?: string;
  expense?: string | null;
  expense_display_id?: string | null;
  reference?: string;
  notes?: string;
  transacted_at: string;
  recorded_by_name?: string;
}

export interface PettyCashTransactionListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: PettyCashTransaction[];
}

export interface ReplenishPettyCashFundResponse {
  transaction: PettyCashTransaction;
  fund: PettyCashFund;
}

export interface Expense {
  id: string;
  display_id?: string;
  expense_number?: string;
  category?: string;
  category_id?: string;
  category_name?: string;
  category_is_vehicle_expense?: boolean;
  vehicle?: string | null;
  vehicle_id?: string | null;
  vehicle_registration?: string | null;
  vehicle_fleet_number?: string | null;
  amount: string;
  expense_date: string;
  description?: string;
  vendor_name?: string;
  reference?: string;
  status: ExpenseStatus;
  status_display?: string;
  payment_source?: ExpensePaymentSource;
  payment_source_display?: string;
  petty_cash_fund?: string | null;
  petty_cash_fund_id?: string | null;
  petty_cash_fund_code?: string | null;
  petty_cash_fund_name?: string | null;
  fuel_order?: string | null;
  fuel_order_display_id?: string | null;
  is_from_fuel_order?: boolean;
  vehicle_maintenance_id?: string | null;
  repair_request_id?: string | null;
  receipt_file?: string | null;
  notes?: string;
  company_name?: string;
  recorded_by_name?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ExpenseListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Expense[];
  summary?: ExpensesSummary;
}

export interface ExpensesSummary {
  total_amount?: string | number;
  total_count?: number;
  by_status?: Record<string, { count?: number; amount?: string | number }>;
  by_category?: Array<{ category_id?: string; category_name?: string; amount?: string | number; count?: number }>;
  [key: string]: unknown;
}

export interface CreateExpenseRequest {
  category_id: string;
  amount: string;
  expense_date: string;
  vehicle_id?: string;
  description?: string;
  vendor_name?: string;
  reference?: string;
  payment_source?: ExpensePaymentSource;
  petty_cash_fund_id?: string;
  vehicle_maintenance_id?: string;
  repair_request_id?: string;
  notes?: string;
}

export interface PettyCashLedgerRow {
  date?: string;
  particulars?: string;
  debit?: string | number | null;
  credit?: string | number | null;
  balance?: string | number | null;
}

export interface PettyCashLedgerSection {
  title?: string;
  registration?: string;
  opening_balance?: string | number;
  closing_balance?: string | number;
  rows?: PettyCashLedgerRow[];
}

export interface PettyCashLedgerResponse {
  title?: string;
  fund?: PettyCashFund;
  general?: PettyCashLedgerSection;
  vehicles?: PettyCashLedgerSection[];
  [key: string]: unknown;
}

export interface TripRoute {
  id: string;
  name?: string;
  source?: string;
  destination?: string;
  origin_name?: string;
  destination_name?: string;
  distance_km?: string | number;
  rate?: string | number;
  is_active?: boolean;
  display_name?: string;
}

export interface TripRouteListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TripRoute[];
}

export interface CreateTripRequest {
  route_id: string;
  product: string;
  quantity: string | number;
  quantity_unit: 'L' | 'kg' | 'tonnes' | 'bags' | 'pallets' | 'units' | 'm³' | 'boxes';
  rate: string | number;
  planned_departure: string;
  prime_mover_id: string;
  trailer_id?: string;
  notes?: string;
}

export interface TripListItem {
  id: string;
  trip_number: string;
  status: string;
  status_display: string;
  route?: { id?: string; source?: string; destination?: string; distance_km?: string } | null;
  origin_name?: string;
  destination_name?: string;
  product?: string;
  quantity?: string;
  quantity_unit?: string;
  rate?: string | number;
  total_amount?: string;
  planned_departure?: string;
  prime_mover?: string;
  prime_mover_registration?: string;
  trailer?: string | null;
  trailer_registration?: string | null;
  driver?: string;
  driver_name?: string;
  notes?: string | null;
  created_at: string;
}

export interface TripDetail extends TripListItem {
  route_id?: string;
  prime_mover_id?: string;
  trailer_id?: string | null;
  actual_departure?: string | null;
  actual_arrival?: string | null;
  updated_at?: string;
}

export interface TripListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TripListItem[];
  summary?: TripsSummary;
}

export interface TripsSummary {
  total: number;
  open: number;
  completed: number;
  in_transit: number;
  by_status?: Record<string, number>;
}

// ─── Trailers, couplings and fleet rigs (VEHICLE_TRAILER_CONFIGURATIONS.md) ──

export interface TrailerType {
  id: string;
  name: string;
  code?: string;
  description?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TrailerTypeListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TrailerType[];
}

export interface Trailer {
  id: string;
  registration_number: string;
  chassis_number?: string | null;
  body_serial_number?: string | null;
  logbook_number?: string | null;
  trailer_type?: string | null;
  trailer_type_name?: string | null;
  make?: string | null;
  model?: string | null;
  year_of_manufacture?: number | null;
  color?: string | null;
  status: string;
  axle_count?: number | null;
  tare_weight_kg?: string | number | null;
  max_payload_kg?: string | number | null;
  max_weight?: string | number | null;
  deck_length_m?: string | number | null;
  tank_capacity_litres?: string | number | null;
  insurance_policy_number?: string | null;
  insurance_expiry?: string | null;
  inspection_expiry?: string | null;
  notes?: string | null;
  current_prime_mover_id?: string | null;
  current_prime_mover_registration?: string | null;
  driver_name?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface TrailerListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Trailer[];
}

export interface CreateTrailerRequest {
  registration_number: string;
  trailer_type?: string;
  chassis_number?: string;
  body_serial_number?: string;
  logbook_number?: string;
  make?: string;
  model?: string;
  year_of_manufacture?: number;
  color?: string;
  status?: string;
  axle_count?: number;
  tare_weight_kg?: string | number;
  max_payload_kg?: string | number;
  deck_length_m?: string | number;
  tank_capacity_litres?: string | number;
  insurance_policy_number?: string;
  insurance_expiry?: string;
  inspection_expiry?: string;
  notes?: string;
}

export interface TrailerCoupling {
  id: string;
  prime_mover: string;
  prime_mover_registration?: string | null;
  trailer: string;
  trailer_registration?: string | null;
  combined_registration?: string | null;
  is_active: boolean;
  coupled_at?: string;
  uncoupled_at?: string | null;
  notes?: string | null;
}

export interface TrailerCouplingListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TrailerCoupling[];
}

export interface FleetRig {
  id: string;
  prime_mover: string;
  prime_mover_registration?: string | null;
  trailer?: string | null;
  trailer_registration?: string | null;
  driver?: string | null;
  driver_id?: string | null;
  driver_name?: string | null;
  combined_registration?: string | null;
  is_active: boolean;
  assigned_at?: string;
  unassigned_at?: string | null;
  notes?: string | null;
}

export interface FleetRigListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: FleetRig[];
}

export interface AssignRigRequest {
  driver_id?: string | null;
  trailer_id?: string | null;
  notes?: string;
}

export interface AssignRigResponse {
  rig: FleetRig;
  vehicle: Vehicle;
  message?: string;
}

export interface CoupleTrailerResponse {
  vehicle: Vehicle;
  coupling: TrailerCoupling;
  message?: string;
}

export interface UncoupleTrailerResponse {
  vehicle?: Vehicle;
  coupling?: TrailerCoupling;
  message?: string;
}

// ─── Trip documents (TRIP_DOCUMENT_UPLOAD.md / TRIP_DOCUMENT_RETRIEVAL.md) ───

export type TripDocumentType =
  | 'waybill'
  | 'delivery_note'
  | 'loading_order'
  | 'weighbridge_ticket'
  | 'product_receipt'
  | 'customs_declaration'
  | 'epra_permit'
  | 'consignment_note'
  | 'pod'
  | 'incident_report'
  | 'other';

export interface TripDocument {
  id: string;
  trip: string;
  document_type: string;
  document_type_display?: string | null;
  label?: string | null;
  notes?: string | null;
  file?: string | null;
  file_url?: string | null;
  original_filename?: string | null;
  uploaded_by?: string | null;
  uploaded_by_name?: string | null;
  created_at: string;
}

export interface TripDocumentListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TripDocument[];
}

export interface UploadTripDocumentRequest {
  trip: string;
  document_type: string;
  file: File;
  label?: string;
  notes?: string;
}

// ─── Fuel station payables & bulk payment (FUEL_PAYMENT / FUEL_BULK_PAYMENT) ─

export interface StationPayableOrder {
  id: string;
  display_id: string;
  station_id: string;
  station_name?: string | null;
  invoice_number?: string | null;
  vehicles_display?: string | null;
  total_amount?: string | null;
  amount_paid?: string | null;
  balance_outstanding: string;
  payment_status?: string | null;
  payment_due_date?: string | null;
  is_overdue?: boolean;
  filled_at?: string | null;
  created_at?: string | null;
}

export interface StationPayable {
  station_id: string;
  station_code?: string | null;
  station_name: string;
  order_count: number;
  opening_outstanding?: string | null;
  total_outstanding: string;
  overdue_outstanding?: string | null;
  orders?: StationPayableOrder[];
}

export interface BulkPayFuelOrdersRequest {
  station_id: string;
  amount: string;
  payment_method_id: string;
  invoice_number?: string;
  reference?: string;
  notes?: string;
  paid_at?: string;
}

export interface FuelOrderBulkPayment {
  id: string;
  fuel_order_display_id?: string | null;
  amount: string;
  invoice_number?: string | null;
  payment_method?: { id?: string; code?: string; name?: string } | null;
  reference?: string | null;
  paid_at?: string | null;
}

export interface FuelOrderBulkPaySnapshot {
  display_id?: string | null;
  payment_status?: string | null;
  balance_outstanding?: string | null;
  amount_paid?: string | null;
}

export interface BulkPayFuelOrdersResponse {
  amount_requested: string;
  amount_allocated: string;
  amount_unallocated?: string;
  total_outstanding_before?: string;
  total_outstanding_after?: string;
  payments?: FuelOrderBulkPayment[];
  fuel_orders?: FuelOrderBulkPaySnapshot[];
  expenses?: any[];
}

export interface ReportDateParams {
  period?: string;
  year?: number | string;
  month?: number | string;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
}

export interface ReportPagination {
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next?: string | null;
  previous?: string | null;
}

export interface FuelOrdersDashboardReportOrder {
  fuel_order_id: string;
  display_id: string;
  station_name?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  status?: string | null;
  payment_status?: string | null;
  payment_terms?: string | null;
  total_amount?: string | null;
  amount_paid?: string | null;
  balance_outstanding?: string | null;
  total_litres?: string | null;
  filled_at?: string | null;
  created_at?: string | null;
}

export interface FuelOrdersDashboardReport {
  summary: {
    total_orders: number;
    total_litres?: string | null;
    total_amount?: string | null;
    total_paid?: string | null;
    balance_outstanding?: string | null;
    by_payment_status?: Record<string, number>;
  };
  orders: FuelOrdersDashboardReportOrder[];
  pagination: ReportPagination;
  period: string;
  generated_at: string;
}

export interface StationCreditStationRow {
  station_id: string;
  station_code?: string | null;
  station_name: string;
  order_count?: number;
  opening_outstanding?: string | null;
  total_supplied?: string | null;
  total_paid?: string | null;
  balance_outstanding?: string | null;
}

export interface StationCreditSupply {
  fuel_order_id: string;
  display_id: string;
  station_id?: string | null;
  station_code?: string | null;
  station_name?: string | null;
  filled_at?: string | null;
  created_at?: string | null;
  total_litres?: string | null;
  total_amount?: string | null;
  amount_paid?: string | null;
  balance_outstanding?: string | null;
  payment_status?: string | null;
  status?: string | null;
  status_code?: string | null;
  payment_due_date?: string | null;
  is_payment_overdue?: boolean;
  invoice_number?: string | null;
  invoice_date?: string | null;
  invoice_no?: string | null;
  vehicle_count?: number;
}

export interface StationCreditPayment {
  payment_id: string;
  fuel_order_id?: string | null;
  fuel_order_display_id?: string | null;
  station_id?: string | null;
  station_code?: string | null;
  station_name?: string | null;
  amount: string;
  invoice_number?: string | null;
  payment_method?: string | null;
  reference?: string | null;
  notes?: string | null;
  paid_by_name?: string | null;
  paid_at?: string | null;
}

export interface StationCreditReport {
  station?: {
    station_id: string;
    station_code?: string | null;
    station_name: string;
    allows_credit?: boolean;
    opening_outstanding?: string | null;
  } | null;
  summary: {
    period_supplied_amount?: string | null;
    period_supplied_litres?: string | null;
    period_supply_count?: number;
    period_paid_amount?: string | null;
    period_payment_count?: number;
    opening_outstanding?: string | null;
    outstanding_balance?: string | null;
    overdue_balance?: string | null;
  };
  stations: StationCreditStationRow[];
  supplies: StationCreditSupply[];
  payments: StationCreditPayment[];
  supplies_pagination?: ReportPagination;
  payments_pagination?: ReportPagination;
  period: string;
  generated_at: string;
}

export interface StationFuelAccountRow {
  date?: string | null;
  station_name?: string | null;
  order_number?: string | null;
  invoice_date?: string | null;
  invoice_no?: string | null;
  truck_no?: string | null;
  unit?: string | number | null;
  rate?: string | number | null;
  total?: string | number | null;
  credit?: string | number | null;
  balance?: string | number | null;
  is_opening?: boolean;
}

export interface StationFuelAccountLedger {
  station_id?: string;
  station_code?: string | null;
  station_name?: string;
  title?: string;
  station_title?: string;
  row_count: number;
  opening_balance?: string | null;
  closing_balance?: string | null;
  totals?: { total?: string | null; credit?: string | null; balance?: string | null };
  rows: StationFuelAccountRow[];
}

export interface StationFuelAccountReport {
  period: string;
  generated_at: string;
  export_filename?: string;
  consolidated?: StationFuelAccountLedger;
  stations: StationFuelAccountLedger[];
}

export interface ExpensesDashboardReportItem {
  expense_id: string;
  display_id?: string | null;
  category_name?: string | null;
  vehicle_registration?: string | null;
  vehicle_fleet_number?: string | null;
  amount: string;
  expense_date?: string | null;
  status?: string | null;
  payment_source?: string | null;
  vendor_name?: string | null;
  description?: string | null;
  is_from_fuel_order?: boolean;
  petty_cash_fund_code?: string | null;
  fuel_order_display_id?: string | null;
}

export interface ExpensesDashboardReport {
  summary: {
    total_expenses: number;
    total_amount?: string | null;
    paid_amount?: string | null;
    by_status?: Record<string, { count: number; amount: string }>;
    by_category?: Array<{
      category_id?: string | null;
      category_name?: string | null;
      count: number;
      amount: string;
    }>;
  };
  expenses: ExpensesDashboardReportItem[];
  pagination: ReportPagination;
  period: string;
  generated_at: string;
}

export interface VehicleExpensesReportVehicle {
  vehicle_id: string;
  fleet_number?: string | null;
  registration_number?: string | null;
  model?: string | null;
  status?: string | null;
  count?: number;
  amount?: string | null;
}

export interface VehicleExpensesReport extends ExpensesDashboardReport {
  vehicle?: VehicleExpensesReportVehicle | null;
  summary: ExpensesDashboardReport['summary'] & {
    fuel_amount?: string | null;
    by_vehicle?: VehicleExpensesReportVehicle[];
  };
}

export interface PettyCashReportFund {
  fund_id: string;
  code?: string | null;
  name: string;
  branch_name?: string | null;
  balance?: string | null;
  float_limit?: string | null;
  period_replenished?: string | null;
  period_spent?: string | null;
}

export interface PettyCashReportTransaction {
  transaction_id: string;
  fund_code?: string | null;
  fund_name?: string | null;
  transaction_type?: string | null;
  amount: string;
  balance_after?: string | null;
  expense_display_id?: string | null;
  reference?: string | null;
  notes?: string | null;
  transacted_at?: string | null;
}

export interface PettyCashDashboardReport {
  summary: {
    total_funds?: number;
    total_balance?: string | null;
    period_replenished?: string | null;
    period_spent?: string | null;
  };
  funds: PettyCashReportFund[];
  transactions: PettyCashReportTransaction[];
  pagination: ReportPagination;
  period: string;
  generated_at: string;
}

/** Nested status object on payroll runs */
export interface PayrollStatus {
  id: string;
  name: string;
  code: string;
  scope?: string;
  module?: string;
  is_active?: boolean;
  description?: string;
}

export interface PayrollRunEmployee {
  id: string;
  user_id: string;
  user_name: string;
  role_name?: string;
  monthly_gross?: string;
  created_at?: string;
}

export interface PayrollRun {
  id: string;
  name: string;
  period_start: string;
  period_end: string;
  status: PayrollStatus | null;
  is_locked: boolean;
  generated_by?: string | null;
  generated_by_name?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  payslip_count: number;
  total_net_pay?: string;
  employee_count: number;
  employees?: PayrollRunEmployee[];
  run_deductions?: PayrollRunDeduction[];
  adjustment_count?: number;
  pending_adjustment_count?: number;
  pending_deduction_count?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  payslip_pdfs_generated?: number;
  payslip_pdfs_queued?: boolean;
}

export interface PayrollRunListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: PayrollRun[];
}

export interface PayrollReportRunRef {
  id: string;
  name: string;
  period_start?: string | null;
  period_end?: string | null;
  status_code?: string | null;
  status_name?: string | null;
}

export interface PayrollDepartmentAnalysisRow {
  department_id?: string | null;
  department_name: string;
  department_code?: string | null;
  employee_count: number;
  gross_earnings: string;
  paye: string;
  nssf: string;
  shif: string;
  housing_levy: string;
  penalties?: string;
  other_deductions?: string;
  total_deductions: string;
  net_pay: string;
}

export interface PayrollStatutoryEmployeeRow {
  payslip_id: string;
  user_id: string;
  employee_name: string;
  payroll_no?: string | null;
  payroll_name?: string | null;
  surname?: string | null;
  last_name?: string | null;
  other_names?: string | null;
  first_name?: string | null;
  id_no?: string | null;
  identity_type?: string | null;
  phone?: string | null;
  kra_pin?: string | null;
  nssf_number?: string | null;
  shif_number?: string | null;
  role_name?: string | null;
  department_id?: string | null;
  department_name: string;
  department_code?: string | null;
  payroll_run?: PayrollReportRunRef | null;
  gross_earnings?: string;
  income?: string | null;
  voluntary?: string | null;
  net_pay?: string;
  amount: string;
  description?: string | null;
  nssf_tier_1?: string | null;
  nssf_tier_2?: string | null;
  paye?: string;
  nssf?: string;
  shif?: string;
  housing_levy?: string;
  penalties?: string;
  other_deductions?: string;
  total_deductions?: string;
}

export interface PayrollDepartmentAnalysisReport {
  report: string;
  generated_at?: string;
  filters?: { payroll_run_id?: string | null; department_id?: string | null };
  summary: {
    department_count: number;
    employee_count: number;
    gross_earnings: string;
    paye: string;
    nssf: string;
    shif: string;
    housing_levy: string;
    penalties?: string;
    other_deductions?: string;
    total_deductions: string;
    net_pay: string;
  };
  departments: PayrollDepartmentAnalysisRow[];
  employees: PayrollStatutoryEmployeeRow[];
}

export interface PayrollStatutoryReport {
  report: string;
  kind: 'paye' | 'nssf' | 'shif' | 'housing_levy' | string;
  label: string;
  description?: string;
  generated_at?: string;
  filters?: { payroll_run_id?: string | null; department_id?: string | null };
  summary: {
    employee_count: number;
    total_amount: string;
    label?: string;
    nssf_tier_1?: string;
    nssf_tier_2?: string;
  };
  employees: PayrollStatutoryEmployeeRow[];
}

export interface PayrollBankTransferEmployeeRow {
  no: number;
  payslip_id: string;
  user_id: string;
  employee_name: string;
  account_number?: string;
  bank_name?: string;
  bank_branch?: string;
  bank_code?: string;
  amount: string;
  month?: string;
  payroll_run?: PayrollReportRunRef | null;
}

export interface PayrollBankTransferReport {
  report: string;
  label: string;
  company_name: string;
  period?: string;
  period_short?: string;
  period_month?: string;
  generated_at?: string;
  filters?: { payroll_run_id?: string | null; department_id?: string | null };
  summary: {
    employee_count: number;
    total_amount: string;
  };
  employees: PayrollBankTransferEmployeeRow[];
}

export interface PayComponentType {
  id: string;
  name: string;
  code: string;
  category: 'earning' | 'deduction' | string;
  description?: string | null;
  is_active: boolean;
  is_system?: boolean;
  is_assignable?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface PayComponentTypeListResponse {
  count?: number;
  results: PayComponentType[];
}

export interface PayrollRunDeduction {
  id: string;
  user_id?: string;
  user_name: string;
  component_type_id?: string;
  component_type_name: string;
  component_type_code?: string;
  amount: string;
  description?: string;
  created_at?: string;
}

export interface CreatePayrollRunRequest {
  name: string;
  period_start: string;
  period_end: string;
  generate?: boolean;
  notes?: string;
  employees?: Array<{ user_id: string }>;
  deductions?: Array<{
    user_id: string;
    component_type_id: string;
    amount: string;
    description?: string;
  }>;
}

export interface PayeBand {
  upper_limit: string | number | null;
  rate_percent: string | number;
}

export interface StatutoryConfig {
  id: string | null;
  effective_from: string;
  nssf_lower_earnings_limit: string;
  nssf_upper_earnings_limit: string;
  nssf_contribution_rate_percent: string;
  shif_rate_percent: string;
  shif_minimum: string;
  housing_levy_rate_percent: string;
  personal_relief: string;
  insurance_relief_rate_percent: string;
  insurance_relief_max: string;
  pension_relief_max: string;
  mortgage_interest_max: string;
  post_retirement_medical_max: string;
  consultant_wht_rate_percent: string;
  paye_bands: PayeBand[];
  notes?: string | null;
  updated_by?: string | null;
  updated_by_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface StatutoryConfigListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results: StatutoryConfig[];
}

export interface StatutoryConfigWrite {
  effective_from: string;
  nssf_lower_earnings_limit: string;
  nssf_upper_earnings_limit: string;
  nssf_contribution_rate_percent: string;
  shif_rate_percent: string;
  shif_minimum: string;
  housing_levy_rate_percent: string;
  personal_relief: string;
  insurance_relief_rate_percent: string;
  insurance_relief_max: string;
  pension_relief_max: string;
  mortgage_interest_max: string;
  post_retirement_medical_max: string;
  consultant_wht_rate_percent: string;
  paye_bands: PayeBand[];
  notes?: string;
}

export interface PayrollGenerateResponse {
  payroll_run: PayrollRun;
  payslips_generated: number;
  skipped_staff?: any[];
  pending_adjustments_warning?: any;
  pending_deductions_warning?: any;
}

export interface Payslip {
  id: string;
  payroll_run?: string;
  payroll_run_name?: string;
  user_id?: string;
  user_name?: string;
  role_name?: string;
  gross_earnings?: string;
  total_deductions?: string;
  net_pay?: string;
  base_salary_total?: string;
  allowance_total?: string;
  pdf_url?: string | null;
  emailed_at?: string | null;
  email_error?: string | null;
  has_email?: boolean;
  loan_repayments_total?: string;
  created_at?: string;
}

export interface PayslipListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Payslip[];
}

export interface PayrollAdjustment {
  id: string;
  user: string;
  user_id: string;
  user_name: string;
  payroll_run?: string | null;
  payroll_run_id?: string | null;
  payroll_run_name?: string | null;
  period_start: string;
  period_end: string;
  adjustment_type: string;
  adjustment_type_display?: string;
  amount: string;
  reason: string;
  reference?: string;
  status: string;
  status_display?: string;
  is_earning?: boolean;
  created_by_name?: string;
  approved_by_name?: string | null;
  approved_at?: string | null;
  rejection_reason?: string | null;
  created_at: string;
}

export interface PayrollAdjustmentListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: PayrollAdjustment[];
}

export interface CreatePayrollAdjustmentRequest {
  user_id: string;
  adjustment_type: string;
  amount: number | string;
  reason: string;
  reference?: string;
  payroll_run_id?: string;
  period_start?: string;
  period_end?: string;
}

export interface PayrollAdjustmentSummaryResponse {
  totals: { count?: number; total_amount?: string | number | null };
  by_type_and_status: Array<{
    adjustment_type: string;
    status: string;
    count: number;
    total?: string | number | null;
  }>;
}

export interface PayrollDeduction {
  id: string;
  user: string;
  user_id: string;
  user_name: string;
  payroll_run?: string | null;
  payroll_run_id?: string | null;
  payroll_run_name?: string | null;
  period_start: string;
  period_end: string;
  deduction_type: string;
  deduction_type_display?: string;
  deduction_mode: string;
  deduction_mode_display?: string;
  title: string;
  incident_description?: string;
  incident_date?: string;
  amount: string;
  total_liability?: string;
  amount_recovered?: string;
  balance_remaining?: string;
  reference?: string;
  status: string;
  status_display?: string;
  created_by_name?: string;
  approved_by_name?: string | null;
  approved_at?: string | null;
  rejection_reason?: string | null;
  created_at: string;
}

export interface PayrollDeductionListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: PayrollDeduction[];
}

export interface LoanRepayment {
  id: string;
  loan?: string;
  loan_reference?: string;
  amount: string;
  source: 'payroll' | 'manual' | string;
  payslip?: string | null;
  notes?: string;
  recorded_by?: string | null;
  recorded_by_name?: string | null;
  created_at: string;
}

export interface StaffLoan {
  id: string;
  reference: string;
  user?: string;
  user_id?: string;
  user_name: string;
  principal: string;
  installment_amount: string;
  amount_repaid: string;
  balance_outstanding: string;
  remaining_installments?: number;
  purpose: string;
  repayment_start?: string | null;
  status: string;
  status_display?: string;
  disbursement_method?: string;
  disbursement_method_display?: string;
  disbursement_reference?: string;
  disbursed_at?: string | null;
  disbursed_by?: string | null;
  disbursed_by_name?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string;
  notes?: string | null;
  repayments?: LoanRepayment[];
  created_at: string;
  updated_at?: string;
}

export interface StaffLoanListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: StaffLoan[];
}

export interface CreateStaffLoanRequest {
  user_id: string;
  principal: string | number;
  installment_amount: string | number;
  purpose: string;
  repayment_start?: string;
  notes?: string;
}

export interface UpcomingLoanDeduction {
  loan_id: string;
  reference: string;
  user_id: string;
  user_name: string;
  principal: string;
  installment_amount: string;
  amount_repaid: string;
  balance_outstanding: string;
  period_deduction: string;
  remaining_installments?: number;
}

export interface UpcomingLoanDeductionResponse {
  period_end: string;
  count: number;
  total_period_deduction: string | number;
  results: UpcomingLoanDeduction[];
}

export interface StaffLoanSummaryResponse {
  totals?: {
    count?: number;
    total_principal?: string | number | null;
    total_repaid?: string | number | null;
  };
  by_status?: Array<{
    status: string;
    count: number;
    total?: string | number | null;
    repaid?: string | number | null;
  }>;
  outstanding?: {
    count?: number;
    total_principal?: string | number | null;
    total_repaid?: string | number | null;
  };
}

export interface AdvanceRepayment {
  id: string;
  advance?: string;
  advance_reference?: string;
  amount: string;
  source: 'payroll' | 'manual' | string;
  payslip?: string | null;
  notes?: string;
  recorded_by?: string | null;
  recorded_by_name?: string | null;
  created_at: string;
}

export interface SalaryAdvance {
  id: string;
  reference: string;
  user?: string;
  user_id?: string;
  user_name: string;
  amount: string;
  reason: string;
  repayment_mode: 'next_payroll' | 'installment' | string;
  repayment_mode_display?: string;
  installment_amount?: string | null;
  amount_repaid: string;
  balance_outstanding: string;
  repayment_start?: string | null;
  status: string;
  status_display?: string;
  disbursement_method?: string;
  disbursement_method_display?: string;
  disbursement_reference?: string;
  disbursed_at?: string | null;
  disbursed_by?: string | null;
  disbursed_by_name?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  approved_by?: string | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  rejected_by?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string;
  notes?: string | null;
  repayments?: AdvanceRepayment[];
  created_at: string;
  updated_at?: string;
}

export interface SalaryAdvanceListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: SalaryAdvance[];
}

export interface CreateSalaryAdvanceRequest {
  user_id: string;
  amount: string | number;
  reason: string;
  repayment_mode?: 'next_payroll' | 'installment' | string;
  installment_amount?: string | number;
  repayment_start?: string;
  notes?: string;
}

export interface UpcomingAdvanceDeduction {
  advance_id: string;
  reference: string;
  user_id: string;
  user_name: string;
  amount: string;
  installment_amount?: string | null;
  amount_repaid: string;
  balance_outstanding: string;
  period_deduction: string;
  repayment_mode?: string;
}

export interface UpcomingAdvanceDeductionResponse {
  period_end: string;
  count: number;
  total_period_deduction: string | number;
  results: UpcomingAdvanceDeduction[];
}

export interface SalaryAdvanceSummaryResponse {
  totals?: {
    count?: number;
    total_requested?: string | number | null;
    total_repaid?: string | number | null;
  };
  by_status?: Array<{
    status: string;
    count: number;
    total?: string | number | null;
    repaid?: string | number | null;
  }>;
  outstanding?: {
    count?: number;
    total_amount?: string | number | null;
    total_repaid?: string | number | null;
  };
}

export interface SalesRevenueChartSeries {
  name: string;
  data: number[];
}

export interface SalesRevenueChartResponse {
  series: SalesRevenueChartSeries[];
  /** X-axis labels (e.g. date labels like "26th Jan"); API may return this instead of categories */
  labels?: string[];
  /** @deprecated Prefer labels from API */
  categories?: string[];
  period: string;
  generated_at: string;
}

export interface TopRetailersChartResponse {
  series: Array<{
    name: string;
    data: number[];
  }>;
  categories: string[];
  period: string;
  generated_at: string;
}

export interface OrdersStatusChartResponse {
  series: number[];
  labels: string[];
  colors: string[];
  period: string;
  generated_at: string;
}

export interface BalesSummaryResponse {
  sold_bales: string;
  pending_delivery_bales: string;
  sold_orders: number;
  pending_delivery_orders: number;
  total_bales: string;
  period: string;
  generated_at: string;
}

/** Dashboard Products Report – products and bundles sold (GET /api/dashboard/products_report/) */
export interface DashboardProductsReportProduct {
  product_name: string;
  category_name: string;
  bundles_sold: string;
  product_id: string;
}

export interface DashboardProductsReportResponse {
  summary: {
    total_products: number;
    total_quantity_sold: string;
    total_revenue: string;
    hub_filter: { hub_id: string; hub_name: string } | null;
    product_type_filter: string;
    unit_type_filter: string;
  };
  products: DashboardProductsReportProduct[];
  pagination: {
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    next: string | null;
    previous: string | null;
  };
  period: string;
  generated_at: string;
}

/** Top Performing Drivers – GET /api/dashboard/top-performing-drivers/ */
export interface TopPerformingDriverItem {
  driver_id: string;
  driver_name: string;
  completed: number;
  bundles_delivered: string;
  daily_average_orders: string;
  daily_average_bundles: string;
}

export interface TopPerformingDriversResponse {
  summary: {
    total_drivers: number;
    completed: number;
    bundles_delivered: string;
    daily_average_orders: string;
    daily_average_bundles: string;
    period: string;
    period_days: number;
    hub_filter: { hub_id: string; hub_name: string };
  };
  drivers: TopPerformingDriverItem[];
  period: string;
  generated_at: string;
}

export interface PaymentStatusItem {
  status: string;
  count: number;
  amount: string;
  percentage: number;
}

export interface PaymentStatusChartData {
  completed: number;
  pending: number;
  failed: number;
}

export interface PaymentStatusDistribution {
  distribution: PaymentStatusItem[];
  total_transactions: number;
  total_amount: string;
  period: string;
  chart_data: PaymentStatusChartData;
}

export interface MonthlyTrendItem {
  month: string;
  year: number;
  amount: string;
  transaction_count: number;
  month_year: string;
}

export interface MonthlyPaymentTrends {
  trends: MonthlyTrendItem[];
  peak_month: string;
  peak_amount: string;
  period: string;
  chart_type: string;
}

export interface GatewayPerformanceItem {
  gateway_name: string;
  gateway_type: string;
  transaction_count: number;
  total_amount: string;
  success_rate: number;
  usage_percentage: number;
}

export interface TopPaymentGateways {
  top_gateways: GatewayPerformanceItem[];
  total_gateways_tracked: number;
  period: string;
}

export interface RecentTransactionItem {
  transaction_id: string;
  customer_name: string;
  phone_number: string;
  amount: string;
  currency: string;
  status: string;
  gateway_name: string;
  date: string | null;
  formatted_date: string;
  bill_ref_number: string | null;
}

export interface RecentTransactions {
  transactions: RecentTransactionItem[];
  total_displayed: number;
  period: string;
}

export interface StkPushAnalytics {
  total_stk_pushes: number;
  safaricom_pushes: number;
  airtel_pushes: number;
  safaricom_percentage: number;
  airtel_percentage: number;
  period: string;
}

export interface CompleteDashboard {
  summary: DashboardSummary;
  payment_status_distribution: PaymentStatusDistribution;
  monthly_trends: MonthlyPaymentTrends;
  top_gateways: TopPaymentGateways;
  recent_transactions: RecentTransactions;
  stk_analytics: StkPushAnalytics;
  generated_at: string;
}

// Company interface matching Django backend model
export interface Company {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  logo?: string; // URL to logo image
  created_at: string;
  updated_at: string;
  hub_count?: number; // Computed field from serializer
  branch_count?: number;
  order_count?: number; // Computed field from serializer
}

export interface CompanyListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Company[];
}

export interface CreateCompanyRequest {
  name: string;
  email?: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  logo?: File;
}

export interface UserCompany {
  id: string;
  user: string;
  company: string;
  role: 'Owner' | 'Admin' | 'Manager' | 'Employee' | 'Viewer';
  status: 'Active' | 'InActive' | 'Deleted';
  created_by?: string;
  created: string;
  updated: string;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  company_name?: string;
  company_email?: string;
  created_by_name?: string;
}

export interface Branch {
  id: string;
  name: string;
  company: string;
  address?: string;
  phone?: string;
  email?: string;
  created: string;
  updated: string;
}

export interface CompanyAnalytics {
  company: Company;
  total_transactions: number;
  total_amount: string;
  success_rate: number;
  growth_percentage: number;
  last_transaction_date?: string;
}

// Company Reports Interfaces (based on REPORTS_SPECIFICATION.md)
export interface CompanyRevenueChartSeries {
  name: string;
  data: number[];
}

export interface CompanyRevenueChartResponse {
  series: CompanyRevenueChartSeries[];
  categories: string[];
  period: string;
  total_revenue: string;
  generated_at: string;
}

export interface CompanyPaymentStatistics {
  payments_count: number;
  total_revenue: string;
  processed_payments: number;
  pending_payments: number;
  total_payments: number;
  unprocessed_mpesa_transactions: number;
  currency: string;
  period: string;
  generated_at: string;
}

// Hub Reports Interfaces (based on REPORTS_SPECIFICATION.md)
export interface HubAnalyticsSummary {
  total_orders: number;
  total_revenue: number;
  completed_orders: number;
  pending_orders: number;
  total_inventory: number;
  low_stock_items: number;
  total_employees: number;
  average_order_value: number;
  period: string;
  generated_at: string;
}

export interface HubRevenueChartResponse {
  series: Array<{
    name: string;
    data: number[];
  }>;
  categories: string[];
  period: string;
  generated_at: string;
}

export interface HubOrdersChartResponse {
  series: Array<{
    name: string;
    data: number[];
  }>;
  categories: string[];
  period: string;
  generated_at: string;
}

export interface HubInventoryStatusChartResponse {
  series: number[];
  labels: string[];
  period: string;
  generated_at: string;
}

// Product Reports Interfaces (based on REPORTS_SPECIFICATION.md)
export interface ProductSalesAnalytics {
  total_sales: number;
  total_revenue: string;
  total_units_sold: number;
  average_order_value: string;
  sales_growth: number;
  top_retailers: number;
  period: string;
  generated_at: string;
}

export interface ProductSalesChartResponse {
  series: Array<{
    name: string;
    data: number[];
  }>;
  categories: string[];
  period: string;
  generated_at: string;
}

// Inventory Reports Interfaces (based on REPORTS_SPECIFICATION.md)
export interface InventoryStockHistoryItem {
  id: string;
  type: string;
  quantity_change: string;
  quantity_before: string;
  quantity_after: string;
  reason: string;
  created_by: string;
  created_by_id: string;
  created_at: string;
}

export interface InventoryStockHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: InventoryStockHistoryItem[];
}

export interface InventoryTransferItem {
  id: string;
  type: string;
  from_hub: string;
  from_hub_id: string;
  to_hub: string;
  to_hub_id: string;
  quantity: string;
  status: string;
  reason: string;
  created_by: string;
  created_at: string;
}

export interface InventoryTransfersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: InventoryTransferItem[];
}

// Income/Sales Reports Interfaces (based on REPORTS_SPECIFICATION.md)
export interface SalesReportSummary {
  total_orders: number;
  total_revenue: string;
  completed_orders: number;
  pending_orders: number;
  total_payments: number;
  completed_payments: number;
  pending_payments: number;
}

export interface OrderStatusItem {
  status: string;
  count: number;
  percentage: number;
}

export interface PaymentMethodItem {
  method: string;
  count: number;
  amount: string;
  percentage: number;
}

// Payment Method interfaces
export interface PaymentMethod {
  id: string;
  name: string;
  code: string;
  description?: string;
  requires_phone: boolean;
  requires_transaction_id: boolean;
  requires_due_date: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethodListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: PaymentMethod[];
}

export interface CreatePaymentMethodRequest {
  name: string;
  code: string;
  description?: string;
  requires_phone?: boolean;
  requires_transaction_id?: boolean;
  requires_due_date?: boolean;
  is_active?: boolean;
}

// Credit terms (for retailer credit dropdowns)
export interface CreditTerm {
  id: string;
  name: string;
  code: string;
  days: number;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Retailer credit account
export interface RetailerCredit {
  id: string;
  retailer: string;
  retailer_id: string;
  credit_limit: string;
  credit_terms?: { id: string; name: string; days: number; [key: string]: unknown } | null;
  credit_terms_id?: string | null;
  is_active: boolean;
  available_credit: string;
  credit_payment_allowed: boolean;
  created_at: string;
  updated_at: string;
}

/** Commission Tier – driver commission by order sequence and optional day of week (GET/POST /api/commission-tiers/) */
export interface CommissionTier {
  id: string;
  name?: string;
  day_of_week: number | null;
  day_of_week_display?: string;
  order_sequence_min: number;
  order_sequence_max: number | null;
  amount: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CommissionTierListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: CommissionTier[];
}

export interface CreateCommissionTierRequest {
  name?: string;
  day_of_week?: number | null;
  order_sequence_min: number;
  order_sequence_max?: number | null;
  amount: string;
  is_active?: boolean;
}

export interface RevenueByHubItem {
  hub: string;
  hub_id: string;
  orders: number;
  revenue: string;
  percentage: number;
}

export interface DailyRevenueItem {
  date: string;
  date_display: string;
  orders: number;
  revenue: string;
}

export interface SalesReportResponse {
  summary: SalesReportSummary;
  orders_by_status: OrderStatusItem[];
  payments_by_method: PaymentMethodItem[];
  revenue_by_hub: RevenueByHubItem[];
  daily_revenue: DailyRevenueItem[];
  period: string;
  generated_at: string;
}

/** GET /api/reports/payment-methods/ – trend + methods for Payment Methods Report page */
export interface PaymentMethodsReportTrendSeries {
  name: string;
  data: number[];
}

export interface PaymentMethodsReportTrend {
  categories: string[];
  series: PaymentMethodsReportTrendSeries[];
}

export interface PaymentMethodsReportMethod {
  name: string;
  revenue: number;
  percentage: number;
}

export interface PaymentMethodsReportResponse {
  period: string;
  trend: PaymentMethodsReportTrend;
  methods: PaymentMethodsReportMethod[];
  generated_at?: string;
}

// Credit Payments Aging – bucket config & report
export interface CreditAgingBucket {
  id: string;
  label: string;
  min_days: number;
  max_days: number | null;
  sort_order: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface CreateCreditAgingBucketRequest {
  label: string;
  min_days: number;
  max_days?: number | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface CreditPaymentsAgingPayment {
  payment_id: string;
  order_id: string;
  order_number: number;
  retailer_id: string;
  retailer_name: string;
  amount: number;
  age_days: number;
  order_date: string;
  due_date?: string;
}

export interface CreditPaymentsAgingBucketResult {
  label: string;
  min_days: number;
  max_days: number | null;
  total_amount: number;
  count: number;
  payments?: CreditPaymentsAgingPayment[];
}

export interface CreditPaymentsAgingResponse {
  buckets: CreditPaymentsAgingBucketResult[];
  summary: { total_outstanding: number; total_count: number };
  generated_at?: string;
}

// Hub interfaces matching Django API
export interface Hub {
  id: string;
  company: string | {
    id: string;
    name: string;
  };
  company_id: string;
  name: string;
  address?: string; // Legacy field
  street?: string;
  county?: string;
  postal_code?: string;
  lat?: number;
  lng?: number;
  order_prefix?: string;
  users_count?: number; // Computed field from serializer
  orders_count?: number; // For top-performing / dashboard display
  created_at: string;
  updated_at: string;
}

export interface HubListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Hub[];
}

export interface CreateHubRequest {
  company_id?: string; // Optional - backend will handle based on logged-in user
  name: string;
  address?: string; // Legacy field
  street?: string;
  county?: string;
  postal_code?: string;
  lat?: number;
  lng?: number;
  order_prefix?: string;
}

// Product interfaces matching Django API
export interface Category {
  id: string;
  name: string;
  description?: string;
  parent?: string | null; // Parent category name (read-only)
  parent_id?: string | null; // Parent category ID (for write operations)
  is_active: boolean;
  children_count?: number; // Number of subcategories
  created_at: string;
  updated_at: string;
}

export interface CreateCategoryRequest {
  name: string;
  description?: string;
  parent_id?: string | null;
  is_active?: boolean;
}

export interface CategoryListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Category[];
}

export interface UnitOfMeasure {
  id: string;
  name: string;
  code: string;
  symbol?: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface UnitOfMeasureListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: UnitOfMeasure[];
}

export interface ProductImage {
  id: string;
  image: string;
  is_primary: boolean;
  alt_text?: string;
  order: number;
  created_at: string;
}

/** Payment-method price (from GET product or list prices by payment method) */
export interface ProductPriceByPaymentMethod {
  id: string;
  product: string;
  product_name?: string;
  payment_method: string;
  payment_method_name?: string;
  payment_method_code?: string;
  price: string;
  created_at?: string;
  updated_at?: string;
}

/** Volume price tier (from GET product or list volume prices). payment_method null = tier for all methods. */
export interface ProductVolumePrice {
  id: string;
  product: string;
  product_name?: string;
  payment_method?: string | null;
  payment_method_name?: string | null;
  payment_method_code?: string | null;
  min_quantity: string;
  unit_price: string;
  created_at?: string;
  updated_at?: string;
}

export interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string | null;
  category?: { id: string; name: string } | null;
  category_id?: string | null;
  /** Flat field from ProductListSerializer */
  category_name?: string;
  unit_price: string;
  cost_price?: string;
  unit_of_measure?: {
    id: string;
    name: string;
    code: string;
  };
  unit_of_measure_id?: string;
  /** Flat field from ProductListSerializer */
  unit_of_measure_code?: string;
  barcode?: string | null;
  image?: string | null;
  is_active: boolean;
  is_trackable: boolean;
  min_stock_level?: string;
  max_stock_level?: string | null;
  weight?: string | null;
  dimensions?: string | null;
  images: ProductImage[];
  prices_by_payment_method?: ProductPriceByPaymentMethod[];
  volume_prices?: ProductVolumePrice[];
  created_at: string;
  updated_at: string;
}

export interface ProductListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Product[];
}

/** Product create/update body. Do not send sku on create (read-only, auto-generated). */
export interface CreateProductRequest {
  name: string;
  description?: string;
  category_id?: string | null;
  unit_price: string;
  cost_price?: string;
  unit_of_measure_id: string;
  barcode?: string;
  is_active?: boolean;
  is_trackable?: boolean;
  min_stock_level?: string;
  max_stock_level?: string;
  weight?: string;
  dimensions?: string;
}

// Inventory interfaces matching Django API
export interface Inventory {
  id: string;
  product: {
    id: string;
    sku: string;
    name: string;
    category_id: string;
    category_name: string;
    unit_price: string;
    unit_of_measure_code: string;
    image?: string;
    is_active: boolean;
  };
  product_id: string;
  hub: string; // StringRelatedField - hub name
  hub_id: string;
  quantity: string;
  reserved_quantity: string;
  available_quantity: string; // Computed field
  is_low_stock: boolean; // Computed field
  last_restocked_at?: string;
  last_sold_at?: string;
  created_at: string;
  updated_at: string;
}

export interface InventoryListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Inventory[];
}

export interface StockItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  category_label: string;
  unit: string;
  unit_label: string;
  unit_symbol?: string;
  unit_cost: string;
  quantity: string;
  min_quantity: string;
  stock_value: string;
  is_active: boolean;
  is_low_stock: boolean;
  is_negative: boolean;
  notes?: string;
  last_restocked_at?: string | null;
  last_issued_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface StockMovement {
  id: string;
  item: string;
  item_name: string;
  item_sku: string;
  item_unit: string;
  movement_type: 'opening' | 'purchase' | 'issue' | 'adjustment' | 'return' | string;
  movement_type_label: string;
  quantity: string;
  quantity_before: string;
  quantity_after: string;
  unit_cost: string;
  total_value: string;
  vehicle?: string | null;
  vehicle_registration?: string | null;
  vehicle_plate: string;
  occurred_on: string;
  reference: string;
  notes: string;
  created_by?: string | null;
  created_by_name: string;
  created_at: string;
}

export interface StockItemSummary {
  item_count: number;
  total_value: string;
  low_stock_count: number;
  negative_count: number;
  categories: { value: string; label: string }[];
  units?: { value: string; label: string }[];
}

export interface StockItemListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: StockItem[];
}

export interface StockMovementListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: StockMovement[];
}

export interface StockActionResponse {
  item: StockItem;
  movement: StockMovement;
  tyres?: TyreAsset[];
}

export interface TyreAsset {
  id: string;
  serial_number: string;
  tyre_type: string;
  tyre_type_label: string;
  status: 'in_stock' | 'fitted' | 'removed' | string;
  status_label: string;
  stock_item?: string | null;
  stock_item_name?: string | null;
  vehicle?: string | null;
  vehicle_registration?: string | null;
  vehicle_plate: string;
  unit_cost: string;
  fitted_on?: string | null;
  removed_on?: string | null;
  supplier?: string;
  invoice_no?: string;
  notes?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
}

export interface TyreSummary {
  total: number;
  fitted: number;
  in_stock: number;
  removed: number;
  by_type: { tyre_type: string; count: number }[];
  types: { value: string; label: string }[];
  statuses: { value: string; label: string }[];
}

export interface TyreListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: TyreAsset[];
}

export interface WorkshopStockReportItem {
  id: string;
  sku: string;
  name: string;
  category: string;
  category_label: string;
  unit: string;
  unit_label: string;
  quantity: string;
  unit_cost: string;
  stock_value: string;
  is_low_stock: boolean;
  is_negative: boolean;
  purchased_qty: string;
  purchased_value: string;
  issued_qty: string;
  issued_value: string;
  adjusted_qty: string;
}

export interface WorkshopStockReportIssue {
  id: string;
  occurred_on: string | null;
  item_id: string;
  item_name: string;
  item_sku: string;
  item_unit: string;
  vehicle_plate: string;
  quantity: string;
  unit_cost: string;
  total_value: string;
  reference: string;
  notes: string;
}

export interface WorkshopStockReport {
  period: string;
  start_date?: string | null;
  end_date?: string | null;
  export_filename: string;
  summary: {
    item_count: number;
    total_value: string;
    low_stock_count: number;
    negative_count: number;
    purchased_qty: string;
    purchased_value: string;
    opening_qty: string;
    opening_value: string;
    issued_qty: string;
    issued_value: string;
    issue_count: number;
    adjusted_qty: string;
    adjusted_value: string;
    returned_qty: string;
    returned_value: string;
  };
  categories: { value: string; label: string }[];
  items: WorkshopStockReportItem[];
  by_category: {
    category: string;
    category_label: string;
    item_count: number;
    quantity: string;
    stock_value: string;
    purchased_value: string;
    issued_value: string;
  }[];
  issues_by_vehicle: {
    vehicle_plate: string;
    issue_count: number;
    issued_qty: string;
    issued_value: string;
  }[];
  monthly_issues: {
    month: string | null;
    month_label: string;
    issue_count: number;
    issued_qty: string;
    issued_value: string;
  }[];
  issues: WorkshopStockReportIssue[];
  issues_truncated: boolean;
  issues_total: number;
}

export interface TyresReportFitting {
  id: string;
  serial_number: string;
  tyre_type: string;
  tyre_type_label: string;
  status: string;
  status_label: string;
  vehicle_plate: string;
  fitted_on?: string | null;
  unit_cost: string;
}

export interface TyresReport {
  period: string;
  start_date?: string | null;
  end_date?: string | null;
  export_filename: string;
  summary: {
    total: number;
    fitted: number;
    in_stock: number;
    removed: number;
    store_qty: string;
    store_value: string;
    vehicle_count: number;
    purchased_qty: string;
    purchased_value: string;
    issued_qty: string;
    issued_value: string;
    issue_count: number;
  };
  types: { value: string; label: string }[];
  by_type: {
    tyre_type: string;
    tyre_type_label: string;
    sku: string;
    serial_count: number;
    fitted: number;
    in_stock: number;
    removed: number;
    store_qty: string;
    store_value: string;
    purchased_qty: string;
    purchased_value: string;
    issued_qty: string;
    issued_value: string;
  }[];
  by_vehicle: {
    vehicle_plate: string;
    total: number;
    all_position: number;
    diff: number;
    front: number;
    terrie: number;
  }[];
  store_items: {
    id: string;
    sku: string;
    name: string;
    quantity: string;
    unit_cost: string;
    stock_value: string;
  }[];
  fittings: TyresReportFitting[];
  fittings_truncated: boolean;
  fittings_total: number;
  issues: WorkshopStockReportIssue[];
  issues_truncated: boolean;
  issues_total: number;
}

// Order interfaces matching Django API
export interface OrderStatus {
  id: string;
  name: string;
  code: string;
  scope?: string;
  module?: string;
}

export interface Order {
  id: string;
  display_id: string; // Display ID like "PMH001"
  order_number: number; // Order number
  hub_name: string; // Branch name (read-only)
  retailer_name: string; // Retailer name (read-only)
  placed_by_name: string; // User name who placed the order (read-only)
  driver_name: string | null; // Driver name (nullable)
  status_name: string; // Status name (read-only)
  status_code: string; // Status code (read-only)
  total_amount: string; // Decimal as string (tax-inclusive)
  tax: string; // Extracted VAT for reporting
  total_bales?: number; // Total bales (optional, from API)
  created_at: string; // ISO datetime
  /** Payments linked to this order (from API payment_methods_read). */
  payment_methods_read?: OrderPaymentMethodRead[];
  // Legacy fields for backward compatibility (optional)
  hub?: string;
  retailer?: string;
  retailer_id?: string;
  placed_by?: string;
  otp_verified?: boolean; // Used on order detail page
  otp_code?: string;
  status?: OrderStatus;
}

export interface OrderListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: Order[];
}

/** GET /api/orders/delivered/chart/ – bundles delivered per day (time-series). */
export interface OrdersDeliveredChartResponse {
  series: Array<{ name: string; data: number[] }>;
  labels: string[];
  period: string;
  generated_at: string;
}

export interface CreateOrderRequestOrderLine {
  product_id: string;
  quantity: number;
  unit_price: string;
}

export interface CreateOrderRequestPaymentMethod {
  payment_method: string;
  amount: string;
}

export interface CreateOrderRequest {
  hub_id: string;
  retailer_id: string;
  driver_id: string;
  order_lines: CreateOrderRequestOrderLine[];
  payment_methods: CreateOrderRequestPaymentMethod[];
}

/** Order payment methods as returned by GET /api/orders/{id}/ (payment_methods_read). */
export interface OrderPaymentMethodRead {
  payment_id: string;
  payment_method_id?: string;
  payment_method_code: string;
  payment_method_name: string;
  amount: string;
  status: string;
  status_name: string;
}

// Stock Requisition – pick list for a hub (GET/POST/PATCH/DELETE /api/stock-requisitions/)
export interface StockRequisitionLine {
  id: string;
  product_id: string;
  product_name: string;
  product_sku?: string;
  quantity: string;
  unit?: string;
}

export interface StockRequisition {
  id: string;
  display_id: string;
  branch_id: string;
  hub_name?: string;
  requisition_number?: number;
  created_by?: string;
  created_by_name?: string;
  notes?: string;
  lines: StockRequisitionLine[];
  created_at: string;
  updated_at: string;
}

/** Create requisition with branch + product lines (GenWatt). */
export interface CreateStockRequisitionRequest {
  branch_id: string;
  notes?: string;
  lines_write: Array<{ product_id: string; quantity: string | number }>;
}

export interface UpdateStockRequisitionRequest {
  branch_id?: string;
  notes?: string;
  lines_write?: Array<{ product_id: string; quantity: string | number }>;
}

/** Request body for POST /api/credit-settlements/ (settle retailer credit). */
export interface CreateCreditSettlementRequest {
  retailer: string; // retailer user UUID
  amount: string;
  payment_method: string; // Cash or M-Pesa payment method UUID
  order?: string; // optional order UUID
  reference?: string;
  notes?: string;
}

export interface CreditSettlement {
  id: string;
  retailer: string;
  amount: string;
  payment_method: string;
  payment_method_name?: string;
  payment_method_code?: string;
  order?: string;
  reference?: string;
  notes?: string;
  created_at?: string;
}

export interface UpdateInventoryRequest {
  quantity?: string;
  reserved_quantity?: string;
}

// Gateway interfaces matching Django models
export interface Gateway {
  id: string;
  name: string;
  gateway_type: string;
  company: string;
  company_name?: string;
  branch: string;
  branch_name?: string;
  mpesa_api: boolean;
  pull_api: boolean;
  status: 'Active' | 'InActive' | 'Deleted';
  created_by: string;
  created_by_name?: string;
  created: string;
  gateway_detail?: GatewayDetail;
  successful_transactions: number;
  total_transaction_value:number;
  total_transactions: number;
  failed_transactions:number;
}

export interface GatewayDetail {
  id: string;
  gateway: string;
  api_key: string;
  ho_no: string;
  store_no: string;
  consumer_key: string;
  consumer_secret: string;
  initiator_name: string;
  initiator_password: string;
  pass_key: string;
  identifier: string;
  mpesa_type: string;
  status: 'Active' | 'InActive' | 'Deleted';
  created_by: string;
  admin_contact: string;
  created: string;
  callback_one: string;
  callback_two: string;
  country: string;
  currency: string;
  version: string;
}

export interface GatewayListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Gateway[];
}

export interface CreateGatewayRequest {
  name: string;
  gateway_type: string;
  company: string;
  branch: string;
  mpesa_api?: boolean;
  pull_api?: boolean;
}

export interface CreateGatewayDetailRequest {
  gateway: string;
  api_key?: string;
  ho_no?: string;
  store_no?: string;
  consumer_key?: string;
  consumer_secret?: string;
  initiator_name?: string;
  initiator_password?: string;
  pass_key?: string;
  identifier?: string;
  mpesa_type?: string;
  admin_contact?: string;
  callback_one?: string;
  callback_two?: string;
  country?: string;
  currency?: string;
  version?: string;
}

export interface CreateCombinedGatewayRequest {
  // Gateway fields
  name: string;
  gateway_type: string;
  company: string;
  branch: string;
  mpesa_api?: boolean;
  pull_api?: boolean;
  // Gateway detail fields
  api_key?: string;
  ho_no?: string;
  store_no?: string;
  consumer_key?: string;
  consumer_secret?: string;
  initiator_name?: string;
  initiator_password?: string;
  pass_key?: string;
  identifier?: string;
  mpesa_type?: string;
  admin_contact?: string;
  callback_one?: string;
  callback_two?: string;
  country?: string;
  currency?: string;
  version?: string;
}

// Pages interfaces matching Django backend
export interface Page {
  id: number;
  name: string;
  display_name: string;
  icon?: string;
  route: string;
  parent?: number;
  order: number;
  status: 'active' | 'inactive' | 'maintenance';
  permission_level: 'public' | 'authenticated' | 'group' | 'admin';
  create: number; // 0 or 1 for permission
  edit: number;   // 0 or 1 for permission
  delete: number; // 0 or 1 for permission
  view: number;   // 0 or 1 for permission
  description?: string;
  comments?: string;
  is_menu_item: boolean;
  is_dashboard_widget: boolean;
  created_by: string;
  created_time: string;
  updated_time: string;
  deleted_time?: string;
  deleted: number;
}

export interface PageListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Page[];
}

export interface CreatePageRequest {
  name: string;
  display_name: string;
  icon?: string;
  route: string;
  parent?: number;
  order?: number;
  status?: string;
  permission_level?: string;
  create?: number;
  edit?: number;
  delete?: number;
  view?: number;
  description?: string;
  comments?: string;
  is_menu_item?: boolean;
  is_dashboard_widget?: boolean;
}

// User interfaces matching Django backend
export interface Role {
  id: string;
  name: string;
  description?: string;
  created_at?: string;
  created_time?: string;
  updated_at?: string;
  updated_time?: string;
  deleted?: number;
  requirements?: RoleRequirement[];
  users_count?: number;
  permissions_count?: number;
}

// Vehicle Management Interfaces
export interface VehicleType {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface Vehicle {
  id: string;
  driver?: User | string | null;
  driver_name?: string | null;
  driver_id?: string | null;
  /** Removed from the API — VehicleType was deleted. Prefer vehicle_kind_display. */
  type?: VehicleType;
  registration_number: string;
  fleet_number?: string | null;
  chassis_number?: string | null;
  engine_number?: string | null;
  logbook_number?: string | null;
  make?: string | null;
  model?: string | null;
  make_model_display?: string | null;
  year_of_manufacture?: number | null;
  color?: string | null;
  status: 'Active' | 'Inactive' | 'Maintenance' | 'Retired';
  image?: string;
  fuel_type?: string;
  fuel_type_display?: string;
  axle_configuration?: string;
  axle_configuration_display?: string;
  engine_power_hp?: number | null;
  tare_weight_kg?: string | number | null;
  gross_vehicle_mass_kg?: string | number | null;
  gross_combination_mass_kg?: string | number | null;
  max_weight?: string | number | null;
  max_volume?: string | number | null;
  insurance_policy_number?: string | null;
  insurance_expiry?: string | null;
  inspection_expiry?: string | null;
  notes?: string | null;
  /** Read-only — always prime_mover */
  vehicle_kind?: string;
  vehicle_kind_display?: string;
  current_trailer_id?: string | null;
  current_trailer_registration?: string | null;
  combined_registration?: string | null;
  active_rig?: FleetRig | null;
  created_at: string;
  updated_at: string;
}

/** POST /api/vehicles/ — all units are prime movers; fleet_number is auto-assigned. */
export interface CreateVehicleRequest {
  registration_number: string;
  chassis_number?: string;
  engine_number?: string;
  logbook_number?: string;
  make?: string;
  model?: string;
  year_of_manufacture?: number;
  color?: string;
  status?: string;
  fuel_type?: string;
  axle_configuration?: string;
  engine_power_hp?: number;
  tare_weight_kg?: string | number;
  gross_vehicle_mass_kg?: string | number;
  gross_combination_mass_kg?: string | number;
  max_weight?: string | number;
  max_volume?: string | number;
  insurance_policy_number?: string;
  insurance_expiry?: string;
  inspection_expiry?: string;
  notes?: string;
  image?: File;
}

export interface VehicleDriverAssignmentRecord {
  id: string;
  vehicle: string;
  vehicle_registration?: string;
  driver: string;
  driver_name?: string;
  assigned_by?: string;
  assigned_by_name?: string;
  assigned_at: string;
  unassigned_at?: string | null;
  unassigned_by?: string | null;
  unassigned_by_name?: string | null;
  notes?: string;
  is_active: boolean;
}

export interface AssignDriverToVehicleResponse {
  vehicle: Vehicle;
  assignment?: VehicleDriverAssignmentRecord;
  rig?: FleetRig;
  message: string;
}

export interface UnassignDriverFromVehicleResponse {
  vehicle: Vehicle;
  rig?: FleetRig;
  message: string;
}

export interface VehicleDriverAssignmentListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: VehicleDriverAssignmentRecord[];
}

export interface VehicleListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Vehicle[];
}

export interface VehicleDriverAssignment {
  id: string;
  vehicle: Vehicle;
  driver: User;
  assigned_by: User;
  assigned_at: string;
  unassigned_at?: string;
  unassigned_by?: User;
  notes?: string;
  is_active: boolean;
}

export interface MaintenanceType {
  id: string;
  name: string;
  description?: string;
  default_interval_km?: number;
  default_interval_days?: number;
  estimated_cost?: number;
  created_at: string;
  updated_at: string;
}

export interface VehicleMaintenance {
  id: string;
  vehicle: Vehicle;
  maintenance_type: MaintenanceType;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date: string;
  completed_date?: string;
  odometer_reading_at_schedule?: number;
  odometer_reading_at_completion?: number;
  cost?: number;
  service_provider?: string;
  description?: string;
  notes?: string;
  invoice_file?: string;
  created_by: User;
  completed_by?: User;
  created_at: string;
  updated_at: string;
}

export interface RepairRequest {
  id: string;
  vehicle: Vehicle;
  driver: User;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'approved' | 'in_progress' | 'completed' | 'rejected' | 'cancelled';
  reported_location?: string;
  odometer_reading?: number;
  issue_images?: string[];
  estimated_cost?: number;
  actual_cost?: number;
  approved_by?: User;
  approved_at?: string;
  service_provider?: string;
  repair_description?: string;
  completed_at?: string;
  completed_by?: User;
  invoice_file?: string;
  created_by: User;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  email?: string;
  phone?: string;
  role?: Role;
  first_name?: string;
  last_name?: string;
  national_id?: string;
  kra_pin?: string;
  nssf_number?: string;
  shif_number?: string;
  has_staff_profile?: boolean;
  street?: string;
  city?: string;
  county?: string;
  postal_code?: string;
  staff_status?: string;
  status?: string;
  branch_id?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  basic_salary?: string | number | null;
  allowances?: string | number | null;
  pay_frequency?: string | null;
  bank_name?: string;
  bank_branch?: string;
  bank_account?: string;
  kin_name?: string;
  kin_relationship?: string;
  kin_phone?: string;
  kin_email?: string;
  image?: string;
  is_active: boolean;
  is_staff: boolean;
  date_joined?: string;
  created_at?: string;
  is_offboarded?: boolean;
  last_working_day?: string | null;
  offboarded_at?: string | null;
  offboard_reason?: string | null;
}

export interface UserListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: User[];
}

/** GET /api/users/me/ – current authenticated user response. */
export interface UserMeResponse {
  id: string;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
  role_name?: string | null;
  role_info?: { id?: string; name?: string } | null;
  first_name?: string | null;
  last_name?: string | null;
  national_id?: string | null;
  kra_pin?: string | null;
  nssf_number?: string | null;
  shif_number?: string | null;
  address?: string | null;
  image?: string | null;
  is_active: boolean;
  last_login?: string | null;
  date_joined?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  lat?: number | null;
  lng?: number | null;
  required_documents?: unknown[];
  verification_documents?: unknown[];
  verification_progress?: { percentage?: number; completed?: number; total?: number; missing?: number; pending?: number };
  missing_documents?: unknown[];
  hubs?: unknown[];
  hubs_detail?: unknown[];
  groups?: unknown[];
  deactivation_reason?: string | null;
  suspension_category?: string | null;
  is_offboarded?: boolean;
  last_working_day?: string | null;
  offboarded_at?: string | null;
  offboard_reason?: string | null;
  offboarded_by_name?: string | null;
}

/** POST /api/users/me/change-password/ request body. */
export interface ChangePasswordMeRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

/** Driver stats – GET /api/users/{user_id}/driver-stats/ */
export interface DriverStatsResponse {
  driver_id: string;
  period: string;
  total_orders: number;
  completed_orders: number;
  pending_orders: number;
  commission: string;
  bundles_delivered: string;
}

/** Disciplinary action (GET /users/{id}/disciplinary-actions/) */
export interface DisciplinaryAction {
  id: string;
  date: string;
  type: string;
  reason: string;
  recorded_by?: string;
}

/** Current pay + history (GET/POST /users/{id}/compensation/) */
export interface StaffCompensationCurrent {
  basic_salary: string | number;
  allowances: string | number;
  pay_frequency: string;
  monthly_gross: string | number;
  effective_from?: string | null;
  employee_number?: string;
}

export interface StaffCompensationHistory {
  id: string;
  basic_salary: string | number;
  allowances: string | number;
  pay_frequency: string;
  monthly_gross: string | number;
  previous_basic_salary?: string | number | null;
  previous_allowances?: string | number | null;
  previous_pay_frequency?: string | null;
  previous_monthly_gross?: string | number | null;
  effective_from: string;
  reason?: string;
  changed_by?: string | null;
  created_at: string;
  is_current?: boolean;
}

export interface StaffCompensationResponse {
  has_staff_profile: boolean;
  current: StaffCompensationCurrent | null;
  history: StaffCompensationHistory[];
}

export interface UpdateStaffCompensationRequest {
  basic_salary: number;
  allowances?: number;
  pay_frequency?: 'Monthly' | 'Bi-weekly';
  effective_from?: string;
  reason?: string;
}

/** Login / sign-in event (GET /users/me/login-activity/ or /users/{id}/login-activity/) */
export interface LoginActivity {
  id: string;
  at: string;
  ip: string;
  device: string;
  location: string;
  status: 'success' | 'failed' | 'unknown';
  method: string;
  user_agent: string;
}

export interface UpdateUserStatusRequest {
  is_active: boolean;
  deactivation_reason?: string;
  suspension_category?: string;
}

export interface OffboardUserRequest {
  last_working_day: string;
  reason: string;
}

export interface HubDriversResponse {
  admin_hubs: Array<{
    hub_id: string;
    hub_name: string;
    driver_count: number;
  }>;
  total_drivers: number;
  drivers: Array<{
    id: string;
    name: string;
    phone: string;
  }>;
}

export interface RetailerResponse {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  orders_count?: number;
  total_bales?: number;
  hubs?: Array<{
    id: string;
    name: string;
  }>;
}

export interface RetailersListResponse {
  count: number;
  results: RetailerResponse[];
  /** @deprecated Use count */
  total_retailers?: number;
  /** @deprecated Use results */
  retailers?: RetailerResponse[];
}

export interface CreateUserRequest {
  email?: string;
  phone?: string;
  role?: number | string;
  first_name?: string;
  last_name?: string;
  national_id?: string;
  kra_pin?: string;
  nssf_number?: string;
  shif_number?: string;
  street?: string;
  city?: string;
  county?: string;
  postal_code?: string;
  staff_status?: string;
  status?: string;
  branch_id?: string;
  department_id?: string;
  basic_salary?: string | number;
  allowances?: string | number;
  pay_frequency?: string;
  bank_name?: string;
  bank_branch?: string;
  bank_account?: string;
  kin_name?: string;
  kin_relationship?: string;
  kin_phone?: string;
  kin_email?: string;
  password: string;
  image?: File;
}

/** POST /api/staff/ – HR onboarding (creates User + StaffProfile for payroll). */
export interface CreateStaffRequest {
  first_name: string;
  last_name: string;
  national_id: string;
  kra_pin: string;
  nssf_number: string;
  shif_number: string;
  phone: string;
  email: string;
  street: string;
  city: string;
  county?: string;
  postal_code?: string;
  role: string;
  branch_id: string;
  department_id?: string;
  department?: string;
  status?: 'Active' | 'On leave';
  basic_salary: string | number;
  allowances?: string | number;
  pay_frequency?: 'Monthly' | 'Bi-weekly';
  bank_name: string;
  bank_branch?: string;
  bank_code?: string;
  bank_account: string;
  kin_name: string;
  kin_relationship?: string;
  kin_phone: string;
  kin_email?: string;
  password?: string;
}

export interface StaffMember {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  national_id?: string;
  kra_pin?: string;
  nssf_number?: string;
  shif_number?: string;
  role?: string | { id?: string; name?: string };
  role_name?: string;
  branch_id?: string;
  branch_name?: string;
  department_id?: string;
  department_name?: string;
  basic_salary?: string;
  allowances?: string;
  monthly_gross?: string;
  employee_number?: string;
  is_active?: boolean;
  last_working_day?: string | null;
  staff_status?: string;
  generated_password?: string;
}

export interface StaffListResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: StaffMember[];
}

export interface Department {
  id: string;
  branch_id?: string;
  branch_name?: string;
  code?: string;
  name: string;
  is_active?: boolean;
}

export interface DepartmentListResponse {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: Department[];
}

export interface CreateRetailerRequest {
  email?: string;
  phone?: string;
  first_name: string;
  last_name: string;
  password: string;
  lat?: number;
  lng?: number;
  hubs: string[];
  role: string;
}

export interface RoleListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Role[];
}

export interface RoleRequirement {
  name: string;
  type: 'file' | 'text';
}

export interface RetailerRequirement {
  name: string;
  document_type: 'Text' | 'File';
}

export interface RetailerRequirementsResponse {
  role: string;
  requirements: RetailerRequirement[];
}

export interface CreateRoleRequest {
  name: string;
  description?: string;
  requirements?: RoleRequirement[];
}

// Payment Request interfaces matching Django backend
export interface PaymentRequest {
  id: string;
  reference_number: string;
  amount: string;
  currency: string;
  payment_method: 'MPESA' | 'CARD' | 'BANK' | 'CASH';
  status: 'Pending' | 'Processing' | 'Completed' | 'Failed' | 'Cancelled';
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  company: string;
  company_name?: string;
  gateway?: string;
  gateway_name?: string;
  description?: string;
  callback_url?: string;
  metadata?: any;
  created_by?: string;
  created_by_name?: string;
  created: string;
  updated: string;
  expires_at?: string;
  status_display?: string;
  payment_method_display?: string;
}

export interface PaymentRequestListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: PaymentRequest[];
}

export interface CreatePaymentRequestRequest {
  reference_number: string;
  amount: string;
  currency?: string;
  payment_method: 'MPESA' | 'CARD' | 'BANK' | 'CASH';
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  company: string;
  gateway?: string;
  description?: string;
  callback_url?: string;
  metadata?: any;
}

// Payment Transaction interfaces
export interface PaymentTransaction {
  id: string;
  payment_request: string;
  payment_request_ref?: string;
  transaction_id: string;
  amount: string;
  currency: string;
  status: 'Pending' | 'Processing' | 'Completed' | 'Failed' | 'Cancelled' | 'Refunded';
  gateway_transaction_id?: string;
  gateway_response?: string;
  gateway_payload?: any;
  created: string;
  updated: string;
  processed_at?: string;
}

export interface PaymentTransactionListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: PaymentTransaction[];
}

// Payment Refund interfaces
export interface PaymentRefund {
  id: string;
  transaction: string;
  transaction_id?: string;
  refund_id: string;
  amount: string;
  reason?: string;
  status: 'Pending' | 'Processing' | 'Completed' | 'Failed';
  gateway_refund_id?: string;
  gateway_response?: string;
  created_by?: string;
  created_by_name?: string;
  created: string;
  processed_at?: string;
  status_display?: string;
}

export interface PaymentRefundListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: PaymentRefund[];
}

export interface CreatePaymentRefundRequest {
  transaction: string;
  amount: string;
  msisdn?: string;
  reason?: string;
}

// Payment interface for DeliverFasta payments
export interface Payment {
  id: string;
  order: string;
  order_id: string;
  amount: string;
  payment_method: {
    id: string;
    name: string;
    code: string;
  };
  payment_method_id: string;
  payment_method_code: string;
  status: {
    id: string;
    name: string;
    code: string;
  };
  status_code: string;
  is_completed: boolean;
  is_pending: boolean;
  is_failed: boolean;
  mpesa_phone_number?: string;
  mpesa_transaction_id?: string;
  cash_receipt_number?: string;
  credit_due_date?: string;
  notes?: string;
  processed_by?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

export interface PaymentListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: Payment[];
}

// Mpesa Transaction (minimal fields for searching by transactionId)
export interface MpesaTransaction {
  id: string;
  transactionId: string;
  msisdn?: string;
  name?: string;
  amount?: string;
  status?: string;
  date?: string | null;
  bill_ref_number?: string | null;
}

export interface MpesaTransactionListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: MpesaTransaction[];
}

// STK Logs Interface
export interface StkLog {
  id: string;
  auto_id: number;
  amount: string;
  customer_name: string;
  invoice_ref: string;
  request_id: string;
  mobile_no: string;
  route_name: string;
  hash: string | null;
  token: string;
  gateway: string;
  gateway_name: string;
  gateway_type: string;
  p_m_type: string;
  pay_bill_no: string;
  response: string;
  created: string;
  created_formatted: string;
}

export interface StkLogsResponse {
  count: number;
  next?: string;
  previous?: string;
  results: StkLog[];
}

// Groups and Permissions interfaces
export interface UserGroup {
  id: number;
  name: string;
  comments?: string;
  created_by: string;
  created_time: string;
  updated_time: string;
  deleted_time?: string;
  deleted: number;
}

export interface UserGroupListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: UserGroup[];
}

export interface CreateUserGroupRequest {
  name: string;
  comments?: string;
}

export interface UserGroupRights {
  id: number;
  page_name: string;
  page: number;
  group: number;
  create: number;
  edit: number;
  view: number;
  delete: number;
  created_time: string;
  updated_time: string;
  deleted_time?: string;
  created_by: string;
  deleted: number;
}

export interface UserGroupRightsListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: UserGroupRights[];
}

export interface CreateUserGroupRightsRequest {
  page: number;
  group: number;
  create?: number;
  edit?: number;
  view?: number;
  delete?: number;
}

export interface UserGroupMember {
  id: number;
  user: string;
  group_info?: {
    id: number;
    name: string;
  };
  group: number;
  active: number;
  created_time: string;
  updated_time: string;
  deleted_time?: string;
  created_by: string;
  deleted: number;
}

export interface UserGroupMemberListResponse {
  count: number;
  next?: string;
  previous?: string;
  results: UserGroupMember[];
}

export interface CreateUserGroupMemberRequest {
  user: string;
  group: number;
  active?: number;
}

export interface UserGroupDetail extends UserGroup {
  permissions: UserGroupRights[];
}

export interface UserDetails extends User {
  status: string;
  name: string;
  groups: UserGroupMember[];
  role_info?: {
    id: number;
    name: string;
    description?: string;
  };
  permissions: PagePermission[];
}

export interface PagePermission {
  page_name: string;
  view: number;
  edit: number;
  create: number;
  delete: number;
}

// Additional Permissions System Interfaces
export interface NavigationItem {
  id: string;
  name: string;
  display_name: string;
  route: string;
  icon: string;
  permissions: {
    create: number;
    edit: number;
    view: number;
    delete: number;
  };
  children: NavigationItem[];
}

export interface NavigationResponse {
  navigation: NavigationItem[];
  user_permissions: { [key: string]: any };
}

export interface DashboardWidget {
  id: string;
  name: string;
  display_name: string;
  route: string;
  icon: string;
  permissions: {
    create: number;
    edit: number;
    view: number;
    delete: number;
  };
}

export interface DashboardWidgetsResponse {
  dashboard_widgets: DashboardWidget[];
}

export interface UserPermission {
  page: {
    id: string;
    name: string;
    display_name: string;
    route: string;
    icon: string;
    status: string;
    permission_level: string;
  };
  permissions: {
    create: number;
    edit: number;
    view: number;
    delete: number;
  };
}

export interface UserPermissionsResponse {
  permissions: UserPermission[];
}

export interface PermissionFlags {
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export type UserPermissionsMap = Record<string, PermissionFlags>;

export type PermissionActionName = 'view' | 'create' | 'edit' | 'delete';

export interface RoleModulePermission extends PermissionFlags {
  module: string;
  label: string;
  group: string;
  parent?: string;
  actions?: PermissionActionName[];
  hint?: string;
}

export interface RolePermissionsMatrix {
  role: {
    id: string;
    name: string;
    is_admin?: boolean;
  };
  permissions: RoleModulePermission[];
}

export interface MyPermissionsResponse {
  role?: string | null;
  role_id?: string | null;
  is_admin: boolean;
  permissions: UserPermissionsMap;
}

export interface RolePermission {
  page: {
    id: string;
    name: string;
    display_name: string;
    route: string;
    icon: string;
  };
  permissions: {
    create: number;
    edit: number;
    view: number;
    delete: number;
  };
}

export interface RolePermissionsResponse {
  role: {
    id: number;
    name: string;
    description?: string;
  };
  permissions: RolePermission[];
}

export interface AddRolePermissionRequest {
  page_name: string;
  create?: number;
  edit?: number;
  view?: number;
  delete?: number;
}

export interface RemoveRolePermissionRequest {
  page_name: string;
}

export interface GroupPermission {
  page: {
    id: string;
    name: string;
    display_name: string;
    route: string;
    icon: string;
  };
  permissions: {
    create: number;
    edit: number;
    view: number;
    delete: number;
  };
}

export interface GroupPermissionsResponse {
  group: {
    id: number;
    name: string;
    comments?: string;
  };
  permissions: GroupPermission[];
}

export interface AddGroupPermissionRequest {
  page_name: string;
  create?: number;
  edit?: number;
  view?: number;
  delete?: number;
}

export interface RemoveGroupPermissionRequest {
  page_name: string;
}

export interface UserGroupMember {
  id: number;
  user: string;
  group: number;
  active: number;
  created_time: string;
  updated_time: string;
  deleted_time?: string;
  created_by: string;
  deleted: number;
}

export interface AddUserToGroupRequest {
  user_id: string;
  group_id: number;
}

export interface RemoveUserFromGroupRequest {
  user_id: string;
  group_id: number;
}

export interface BulkAddUsersToGroupRequest {
  user_ids: string[];
  group_id: number;
}

export interface BulkRemoveUsersFromGroupRequest {
  user_ids: string[];
  group_id: number;
}

export interface CheckPermissionRequest {
  user_id?: string;
  page_name: string;
  permission_type: 'create' | 'edit' | 'view' | 'delete';
}

export interface CheckPermissionResponse {
  user_id: string;
  page_name: string;
  permission_type: string;
  has_permission: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ApiInterfaceService {
  private baseUrl = endpoint;
  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json'
    });
  }

  private getFormDataHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('API Error:', error);
    return throwError(() => error);
  }

  // Company CRUD Operations
  getCompanies(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    city?: string,
    state?: string,
    country?: string,
    ordering?: string 
  }): Observable<CompanyListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<CompanyListResponse>(`${this.baseUrl}/companies/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompany(id: string): Observable<Company> {
    return this.http.get<Company>(`${this.baseUrl}/companies/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getMyCompany(): Observable<Company> {
    return this.http.get<Company>(`${this.baseUrl}/companies/me/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createCompany(company: CreateCompanyRequest): Observable<Company> {
    const formData = new FormData();
    
    // Add company fields
    Object.entries(company).forEach(([key, value]) => {
      if (key === 'logo' && value instanceof File) {
        formData.append('logo', value);
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }
    });

    return this.http.post<Company>(`${this.baseUrl}/companies/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateCompany(id: string, company: Partial<CreateCompanyRequest>): Observable<Company> {
    const formData = new FormData();
    
    Object.entries(company).forEach(([key, value]) => {
      if (key === 'logo' && value instanceof File) {
        formData.append('logo', value);
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }
    });

    return this.http.patch<Company>(`${this.baseUrl}/companies/${id}/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateMyCompany(company: Partial<CreateCompanyRequest>): Observable<Company> {
    const formData = new FormData();

    Object.entries(company).forEach(([key, value]) => {
      if (key === 'logo' && value instanceof File) {
        formData.append('logo', value);
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }
    });

    return this.http.patch<Company>(`${this.baseUrl}/companies/me/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteCompany(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/companies/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Hub Operations
  getHubs(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string,
    company?: string,
    ordering?: string 
  }): Observable<HubListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<HubListResponse>(`${this.baseUrl}/branches/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getHub(id: string): Observable<Hub> {
    return this.http.get<Hub>(`${this.baseUrl}/branches/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createHub(hub: CreateHubRequest): Observable<Hub> {
    return this.http.post<Hub>(`${this.baseUrl}/branches/`, hub, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createRetailer(retailer: CreateRetailerRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/users/create-retailer/`, retailer, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createRetailerWithFormData(formData: FormData): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/users/create-retailer/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateHub(id: string, hub: Partial<CreateHubRequest>): Observable<Hub> {
    return this.http.patch<Hub>(`${this.baseUrl}/branches/${id}/`, hub, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteHub(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/branches/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Hub Reports (based on REPORTS_SPECIFICATION.md)
  getHubAnalytics(
    hubId: string,
    period: string
  ): Observable<HubAnalyticsSummary> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    
    return this.http.get<HubAnalyticsSummary>(
      `${this.baseUrl}/branches/${hubId}/analytics/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getHubRevenueChart(
    hubId: string,
    period: string
  ): Observable<HubRevenueChartResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    
    return this.http.get<HubRevenueChartResponse>(
      `${this.baseUrl}/branches/${hubId}/revenue-chart/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getHubOrdersChart(
    hubId: string,
    period: string
  ): Observable<HubOrdersChartResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    
    return this.http.get<HubOrdersChartResponse>(
      `${this.baseUrl}/branches/${hubId}/orders-chart/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getHubInventoryStatusChart(
    hubId: string
  ): Observable<HubInventoryStatusChartResponse> {
    return this.http.get<HubInventoryStatusChartResponse>(
      `${this.baseUrl}/branches/${hubId}/inventory-status-chart/`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Product Operations
  getProducts(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string,
    category?: string,
    is_active?: boolean,
    is_trackable?: boolean,
    ordering?: string 
  }): Observable<ProductListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<ProductListResponse>(`${this.baseUrl}/products/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getProduct(id: string): Observable<Product> {
    return this.http.get<Product>(`${this.baseUrl}/products/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createProduct(product: CreateProductRequest, imageFile?: File): Observable<Product> {
    // Build payload: category_id null when empty; do not send sku (read-only)
    const payload: Record<string, unknown> = {
      name: product.name,
      unit_price: product.unit_price,
      unit_of_measure_id: product.unit_of_measure_id,
      is_active: product.is_active !== undefined ? product.is_active : true,
      is_trackable: product.is_trackable !== undefined ? product.is_trackable : false
    };
    if (product.description != null && product.description !== '') {
      payload['description'] = product.description;
    }
    payload['category_id'] = product.category_id || null;
    if (product.cost_price != null && product.cost_price !== '') {
      payload['cost_price'] = product.cost_price;
    }
    if (product.barcode != null && product.barcode !== '') {
      payload['barcode'] = product.barcode;
    }
    if (product.min_stock_level != null && product.min_stock_level !== '') {
      payload['min_stock_level'] = product.min_stock_level;
    }
    if (product.max_stock_level != null && product.max_stock_level !== '') {
      payload['max_stock_level'] = product.max_stock_level;
    }
    if (product.weight != null && product.weight !== '') {
      payload['weight'] = product.weight;
    }
    if (product.dimensions != null && product.dimensions !== '') {
      payload['dimensions'] = product.dimensions;
    }

    if (imageFile) {
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value === true || value === false ? String(value) : String(value));
        }
      });
      formData.append('image', imageFile);
      return this.http.post<Product>(`${this.baseUrl}/products/`, formData, {
        headers: this.getFormDataHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    }
    return this.http.post<Product>(`${this.baseUrl}/products/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateProduct(id: string, product: Partial<CreateProductRequest>): Observable<Product> {
    return this.http.patch<Product>(`${this.baseUrl}/products/${id}/`, product, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteProduct(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/products/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Product prices by payment method (GET /api/product-prices-by-payment-method/?product={id})
  getProductPricesByPaymentMethod(productId: string): Observable<ProductPriceByPaymentMethod[]> {
    const params = new URLSearchParams();
    params.set('product', productId);
    return this.http.get<ProductPriceByPaymentMethod[]>(
      `${this.baseUrl}/product-prices-by-payment-method/?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map((res: any) => Array.isArray(res) ? res : (res?.results ?? [])),
      catchError(this.handleError)
    );
  }

  createProductPriceByPaymentMethod(body: { product: string; payment_method: string; price: string }): Observable<ProductPriceByPaymentMethod> {
    return this.http.post<ProductPriceByPaymentMethod>(
      `${this.baseUrl}/product-prices-by-payment-method/`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  updateProductPriceByPaymentMethod(id: string, body: Partial<{ product: string; payment_method: string; price: string }>): Observable<ProductPriceByPaymentMethod> {
    return this.http.patch<ProductPriceByPaymentMethod>(
      `${this.baseUrl}/product-prices-by-payment-method/${id}/`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  deleteProductPriceByPaymentMethod(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/product-prices-by-payment-method/${id}/`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /**
   * Product volume prices – backend under /api/products/product-volume-prices/
   * GET    ?product={id}&payment_method={uuid}  – list tiers; optional filter by payment method
   * POST   body: { product, payment_method?: uuid | null, min_quantity, unit_price }  – null = tier for all methods
   * PATCH  {tier_uuid}/  – body: { payment_method?: string | null, min_quantity?, unit_price? }
   * DELETE {tier_uuid}/
   */
  getProductVolumePrices(productId: string, paymentMethodId?: string): Observable<ProductVolumePrice[]> {
    const params = new URLSearchParams();
    params.set('product', productId);
    if (paymentMethodId) params.set('payment_method', paymentMethodId);
    return this.http.get<ProductVolumePrice[]>(
      `${this.baseUrl}/products/product-volume-prices/?${params.toString()}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map((res: any) => Array.isArray(res) ? res : (res?.results ?? [])),
      catchError(this.handleError)
    );
  }

  createProductVolumePrice(body: { product: string; payment_method?: string | null; min_quantity: string; unit_price: string }): Observable<ProductVolumePrice> {
    const payload: Record<string, string | null> = {
      product: body.product,
      min_quantity: String(body.min_quantity),
      unit_price: String(body.unit_price),
    };
    if (body.payment_method !== undefined) payload['payment_method'] = body.payment_method || null;
    return this.http.post<ProductVolumePrice>(
      `${this.baseUrl}/products/product-volume-prices/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  updateProductVolumePrice(id: string, body: { payment_method?: string | null; min_quantity?: string; unit_price?: string }): Observable<ProductVolumePrice> {
    const payload: Record<string, string | null> = {};
    if (body.payment_method !== undefined) payload['payment_method'] = body.payment_method ?? null;
    if (body.min_quantity !== undefined) payload['min_quantity'] = String(body.min_quantity);
    if (body.unit_price !== undefined) payload['unit_price'] = String(body.unit_price);
    return this.http.patch<ProductVolumePrice>(
      `${this.baseUrl}/products/product-volume-prices/${id}/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  deleteProductVolumePrice(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/products/product-volume-prices/${id}/`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // Product Reports (based on REPORTS_SPECIFICATION.md)
  getProductSalesAnalytics(
    productId: string,
    period: string
  ): Observable<ProductSalesAnalytics> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    
    return this.http.get<ProductSalesAnalytics>(
      `${this.baseUrl}/products/${productId}/sales-analytics/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getProductSalesChart(
    productId: string,
    period: string
  ): Observable<ProductSalesChartResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', period);
    
    return this.http.get<ProductSalesChartResponse>(
      `${this.baseUrl}/products/${productId}/sales-chart/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Category Operations
  getCategories(params?: {
    is_active?: boolean,
    search?: string,
    parent?: string,
    parent_only?: boolean,
    child_only?: boolean,
    page?: number,
    page_size?: number,
    ordering?: string
  }): Observable<CategoryListResponse> {
    return this.http.get<CategoryListResponse>(`${this.baseUrl}/categories/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getCategory(id: string): Observable<Category> {
    return this.http.get<Category>(`${this.baseUrl}/categories/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createCategory(category: CreateCategoryRequest): Observable<Category> {
    return this.http.post<Category>(`${this.baseUrl}/categories/`, category, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateCategory(id: string, category: Partial<CreateCategoryRequest>): Observable<Category> {
    return this.http.patch<Category>(`${this.baseUrl}/categories/${id}/`, category, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/categories/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Unit of Measure Operations
  getUnitsOfMeasure(params?: { 
    is_active?: boolean,
    search?: string 
  }): Observable<UnitOfMeasure[]> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<UnitOfMeasureListResponse>(`${this.baseUrl}/units-of-measure/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((response) => response.results || []),
      catchError(this.handleError)
    );
  }

  // Inventory Operations
  getInventory(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string,
    product?: string,
    hub?: string,
    low_stock?: boolean 
  }): Observable<InventoryListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<InventoryListResponse>(`${this.baseUrl}/inventory/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getInventoryItem(id: string): Observable<Inventory> {
    return this.http.get<Inventory>(`${this.baseUrl}/inventory/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateInventory(id: string, inventory: UpdateInventoryRequest): Observable<Inventory> {
    return this.http.patch<Inventory>(`${this.baseUrl}/inventory/${id}/`, inventory, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Inventory Reports (based on REPORTS_SPECIFICATION.md)
  getInventoryStockHistory(
    inventoryId: string,
    params?: {
      transaction_type?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }
  ): Observable<InventoryStockHistoryResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<InventoryStockHistoryResponse>(
      `${this.baseUrl}/inventory/${inventoryId}/stock-history/${httpParams}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getInventoryTransfers(
    inventoryId: string,
    params?: {
      type?: string;
      status?: string;
      page?: number;
      page_size?: number;
    }
  ): Observable<InventoryTransfersResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<InventoryTransfersResponse>(
      `${this.baseUrl}/inventory/${inventoryId}/transfers/${httpParams}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getStockItems(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    category?: string;
    low_stock?: boolean;
    ordering?: string;
  }): Observable<StockItemListResponse> {
    return this.http.get<StockItemListResponse>(
      `${this.baseUrl}/stock-items/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getStockItemSummary(): Observable<StockItemSummary> {
    return this.http.get<StockItemSummary>(`${this.baseUrl}/stock-items/summary/`, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  getStockItem(id: string): Observable<StockItem> {
    return this.http.get<StockItem>(`${this.baseUrl}/stock-items/${id}/`, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  createStockItem(payload: Partial<StockItem>): Observable<StockItem> {
    return this.http.post<StockItem>(`${this.baseUrl}/stock-items/`, payload, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  seedWorkshopStock(): Observable<{
    items: number;
    created_items: number;
    opening_movements: number;
    tyres?: { created: number; updated: number; total: number };
  }> {
    return this.http.post<{ items: number; created_items: number; opening_movements: number }>(
      `${this.baseUrl}/stock-items/seed/`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getStockItemMovements(
    id: string,
    params?: {
      page?: number;
      page_size?: number;
      movement_type?: string;
      start_date?: string;
      end_date?: string;
    }
  ): Observable<StockMovementListResponse> {
    return this.http.get<StockMovementListResponse>(
      `${this.baseUrl}/stock-items/${id}/movements/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  purchaseStockItem(id: string, payload: {
    quantity: number | string;
    unit_cost?: number | string;
    occurred_on?: string;
    reference?: string;
    notes?: string;
  }): Observable<StockActionResponse> {
    return this.http.post<StockActionResponse>(`${this.baseUrl}/stock-items/${id}/purchase/`, payload, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  issueStockItem(id: string, payload: {
    quantity: number | string;
    vehicle_plate?: string;
    vehicle_id?: string;
    occurred_on?: string;
    reference?: string;
    notes?: string;
    serial_numbers?: string;
  }): Observable<StockActionResponse> {
    return this.http.post<StockActionResponse>(`${this.baseUrl}/stock-items/${id}/issue/`, payload, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  adjustStockItem(id: string, payload: {
    counted_quantity?: number | string;
    quantity_delta?: number | string;
    occurred_on?: string;
    reference?: string;
    notes?: string;
    reason?: string;
  }): Observable<StockActionResponse> {
    return this.http.post<StockActionResponse>(`${this.baseUrl}/stock-items/${id}/adjust/`, payload, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  getTyres(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    tyre_type?: string;
    status?: string;
    vehicle_plate?: string;
    vehicle?: string;
  }): Observable<TyreListResponse> {
    return this.http.get<TyreListResponse>(
      `${this.baseUrl}/tyres/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getTyreSummary(): Observable<TyreSummary> {
    return this.http.get<TyreSummary>(`${this.baseUrl}/tyres/summary/`, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  getWorkshopStockReport(params?: {
    period?: string;
    category?: string;
    vehicle_plate?: string;
    vehicle_id?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<WorkshopStockReport> {
    return this.http.get<WorkshopStockReport>(
      `${this.baseUrl}/stock-items/report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportWorkshopStockReport(params?: {
    period?: string;
    category?: string;
    vehicle_plate?: string;
    vehicle_id?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/stock-items/report-export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  getTyresReport(params?: {
    period?: string;
    tyre_type?: string;
    vehicle_plate?: string;
    vehicle_id?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<TyresReport> {
    return this.http.get<TyresReport>(
      `${this.baseUrl}/tyres/report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportTyresReport(params?: {
    period?: string;
    tyre_type?: string;
    vehicle_plate?: string;
    vehicle_id?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/tyres/report-export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  getTyre(id: string): Observable<TyreAsset> {
    return this.http.get<TyreAsset>(`${this.baseUrl}/tyres/${id}/`, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  createTyre(payload: Partial<TyreAsset>): Observable<TyreAsset> {
    return this.http.post<TyreAsset>(`${this.baseUrl}/tyres/`, payload, {
      headers: this.getAuthHeaders(),
    }).pipe(catchError(this.handleError));
  }

  fitTyre(id: string, payload: {
    vehicle_plate?: string;
    vehicle_id?: string;
    occurred_on?: string;
    notes?: string;
    update_stock?: boolean;
  }): Observable<{ tyre: TyreAsset; movement: StockMovement | null }> {
    return this.http.post<{ tyre: TyreAsset; movement: StockMovement | null }>(
      `${this.baseUrl}/tyres/${id}/fit/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  removeTyre(id: string, payload: {
    return_to_store?: boolean;
    occurred_on?: string;
    notes?: string;
  }): Observable<{ tyre: TyreAsset; movement: StockMovement | null }> {
    return this.http.post<{ tyre: TyreAsset; movement: StockMovement | null }>(
      `${this.baseUrl}/tyres/${id}/remove/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // Income/Sales Reports (based on REPORTS_SPECIFICATION.md)
  getSalesReport(
    params: {
      period: string;
      hub_id?: string;
    }
  ): Observable<SalesReportResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('period', params.period);
    
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    
    return this.http.get<SalesReportResponse>(
      `${this.baseUrl}/reports/sales/?${queryParams.toString()}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** GET /api/reports/payment-methods/ – trend (area chart) + methods (performance breakdown) */
  getPaymentMethodsReport(params: {
    period: string;
    start_date?: string;
    end_date?: string;
    payment_methods?: string;
  }): Observable<PaymentMethodsReportResponse> {
    const queryParams = new URLSearchParams();
    queryParams.set('period', params.period);
    if (params.start_date) {
      queryParams.set('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.set('end_date', params.end_date);
    }
    if (params.payment_methods) {
      queryParams.set('payment_methods', params.payment_methods);
    }
    return this.http.get<PaymentMethodsReportResponse>(
      `${this.baseUrl}/reports/payment-methods/?${queryParams.toString()}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** GET /api/reports/credit-payments-aging/ */
  getCreditPaymentsAgingReport(params?: {
    retailer_id?: string;
    hub_id?: string;
    include_items?: boolean;
  }): Observable<CreditPaymentsAgingResponse> {
    const queryParams = new URLSearchParams();
    if (params?.retailer_id) queryParams.set('retailer_id', params.retailer_id);
    if (params?.hub_id) queryParams.set('hub_id', params.hub_id);
    if (params?.include_items === true) queryParams.set('include_items', 'true');
    const qs = queryParams.toString();
    return this.http.get<CreditPaymentsAgingResponse>(
      `${this.baseUrl}/reports/credit-payments-aging/${qs ? '?' + qs : ''}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** GET /api/credit-aging-buckets/ */
  getCreditAgingBuckets(params?: { is_active?: boolean }): Observable<CreditAgingBucket[]> {
    const queryParams = new URLSearchParams();
    if (params?.is_active === true) queryParams.set('is_active', 'true');
    const qs = queryParams.toString();
    return this.http.get<CreditAgingBucket[] | { results: CreditAgingBucket[] }>(
      `${this.baseUrl}/credit-aging-buckets/${qs ? '?' + qs : ''}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map((res) => Array.isArray(res) ? res : (res?.results ?? [])),
      retry(2),
      catchError(this.handleError)
    );
  }

  /** GET /api/credit-aging-buckets/<id>/ */
  getCreditAgingBucket(id: string): Observable<CreditAgingBucket> {
    return this.http.get<CreditAgingBucket>(
      `${this.baseUrl}/credit-aging-buckets/${id}/`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** POST /api/credit-aging-buckets/ */
  createCreditAgingBucket(body: CreateCreditAgingBucketRequest): Observable<CreditAgingBucket> {
    return this.http.post<CreditAgingBucket>(
      `${this.baseUrl}/credit-aging-buckets/`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /** PATCH /api/credit-aging-buckets/<id>/ */
  updateCreditAgingBucket(id: string, body: Partial<CreateCreditAgingBucketRequest>): Observable<CreditAgingBucket> {
    return this.http.patch<CreditAgingBucket>(
      `${this.baseUrl}/credit-aging-buckets/${id}/`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /** DELETE /api/credit-aging-buckets/<id>/ */
  deleteCreditAgingBucket(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/credit-aging-buckets/${id}/`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // Stock Requisitions (GET/POST/PATCH/DELETE /api/stock-requisitions/)
  getStockRequisitions(params?: { branch?: string; page?: number; page_size?: number; ordering?: string; search?: string }): Observable<{ count: number; results: StockRequisition[] }> {
    return this.http.get<StockRequisition[] | { count?: number; results?: StockRequisition[] }>(
      `${this.baseUrl}/stock-requisitions/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map((res) => {
        if (Array.isArray(res)) {
          return { count: res.length, results: res };
        }
        const results = res?.results ?? [];
        return { count: res?.count ?? results.length, results };
      }),
      catchError(this.handleError)
    );
  }

  getStockRequisition(id: string): Observable<StockRequisition> {
    return this.http.get<StockRequisition>(`${this.baseUrl}/stock-requisitions/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createStockRequisition(body: CreateStockRequisitionRequest): Observable<StockRequisition> {
    return this.http.post<StockRequisition>(`${this.baseUrl}/stock-requisitions/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateStockRequisition(id: string, body: UpdateStockRequisitionRequest): Observable<StockRequisition> {
    return this.http.patch<StockRequisition>(`${this.baseUrl}/stock-requisitions/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteStockRequisition(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/stock-requisitions/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** GET /api/stock-requisitions/<id>/stock-requisition-pdf/ – download PDF (blob). */
  getStockRequisitionPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/stock-requisitions/${id}/stock-requisition-pdf/`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // Order Operations
  getOrders(params?: {
    page?: number;
    page_size?: number;
    hub?: string;
    retailer?: string;
    retailer_search?: string;
    status?: string;
    status_code?: string;
    date_from?: string;
    date_to?: string;
    min_total?: number;
    max_total?: number;
    otp_verified?: boolean;
    search?: string;
    ordering?: string;
    driver_id?: string;
  }): Observable<OrderListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<OrderListResponse>(`${this.baseUrl}/orders/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getOrder(id: string): Observable<Order> {
    return this.http.get<Order>(`${this.baseUrl}/orders/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createOrder(order: CreateOrderRequest): Observable<Order> {
    return this.http.post<Order>(`${this.baseUrl}/orders/`, order, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateOrder(id: string, order: Partial<CreateOrderRequest>): Observable<Order> {
    return this.http.patch<Order>(`${this.baseUrl}/orders/${id}/`, order, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteOrder(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/orders/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * POST /api/credit-settlements/ – record a credit settlement (retailer pays by Cash/M-Pesa).
   */
  createCreditSettlement(body: CreateCreditSettlementRequest): Observable<CreditSettlement> {
    return this.http.post<CreditSettlement>(`${this.baseUrl}/credit-settlements/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * GET /api/credit-settlements/?retailer_id={id}&order={order_id}
   */
  getCreditSettlements(params?: { retailer_id?: string; order_id?: string }): Observable<{ results: CreditSettlement[] }> {
    const queryParams = new URLSearchParams();
    if (params?.retailer_id) queryParams.set('retailer_id', params.retailer_id);
    if (params?.order_id) queryParams.set('order', params.order_id);
    const q = queryParams.toString();
    return this.http.get<{ results: CreditSettlement[] }>(
      `${this.baseUrl}/credit-settlements/${q ? '?' + q : ''}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * GET /api/orders/delivered/chart/ – bundles delivered per day for delivered/completed orders.
   * Params: period, start_date, end_date, hub_id, driver_id, retailer_id.
   */
  getOrdersDeliveredChart(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    hub_id?: string;
    driver_id?: string;
    retailer_id?: string;
  }): Observable<OrdersDeliveredChartResponse> {
    const queryParams: string[] = [];
    if (params?.period) queryParams.push(`period=${encodeURIComponent(params.period)}`);
    if (params?.start_date) queryParams.push(`start_date=${params.start_date}`);
    if (params?.end_date) queryParams.push(`end_date=${params.end_date}`);
    if (params?.hub_id) queryParams.push(`hub_id=${encodeURIComponent(params.hub_id)}`);
    if (params?.driver_id) queryParams.push(`driver_id=${encodeURIComponent(params.driver_id)}`);
    if (params?.retailer_id) queryParams.push(`retailer_id=${encodeURIComponent(params.retailer_id)}`);
    const qs = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.http.get<OrdersDeliveredChartResponse>(
      `${this.baseUrl}/orders/delivered/chart/${qs}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  // User Company Operations
  getUserCompanies(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string,
    status?: string 
  }): Observable<{ count: number; results: UserCompany[] }> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<{ count: number; results: UserCompany[] }>(`${this.baseUrl}/user_companies/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompanyUsers(companyId: string): Observable<UserCompany[]> {
    return this.http.get<UserCompany[]>(`${this.baseUrl}/companies/${companyId}/users/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompanyPaymentStats(
    companyId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      hub_id?: string;
    }
  ): Observable<CompanyPaymentStatistics> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<CompanyPaymentStatistics>(
      `${this.baseUrl}/companies/${companyId}/payment-stats/${httpParams}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompanyRevenueChart(
    companyId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      hub_id?: string;
      period?: string;
    }
  ): Observable<CompanyRevenueChartResponse> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<CompanyRevenueChartResponse>(
      `${this.baseUrl}/companies/${companyId}/revenue-chart/${httpParams}`,
      {
        headers: this.getAuthHeaders()
      }
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompanyPaymentsReport(companyId: string, params?: {
    gateway_id?: string;
    currency?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<{
    data: {
      total_revenue: string;
      processed_transactions: number;
      total_payments: number;
      unprocessed_mpesa_transactions: number;
      currency: string;
      period: string;
      start_date: string;
      end_date: string;
      generated_at: string;
    };
    status: string;
  }> {
    const queryParams = new URLSearchParams();
    
    // Add company_id as a search parameter
    queryParams.append('company_id', companyId);
    
    // Add other parameters if provided
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<{
      data: {
        total_revenue: string;
        processed_transactions: number;
        total_payments: number;
        unprocessed_mpesa_transactions: number;
        currency: string;
        period: string;
        start_date: string;
        end_date: string;
        generated_at: string;
      };
      status: string;
    }>(`${this.baseUrl}/reports/dashboard/company-payments-report/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentProcessingReport(params?: {
    time_period?: string;
    currency?: string;
  }): Observable<{
    data: {
      chart_data: {
        categories: string[];
        series: { name: string; type: string; data: number[] }[];
        period_label: string;
      };
      summary_stats: {
        total_revenue: string;
        processed_transactions: number;
        total_payments: number;
        unprocessed_mpesa_transactions: number;
        currency: string;
        period: string;
        start_date: string;
        end_date: string;
        generated_at: string;
      };
    };
    status: string;
  }> {
    const queryParams = new URLSearchParams();
    
    // Add parameters if provided
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    
    return this.http.get<{
      data: {
        chart_data: {
          categories: string[];
          series: { name: string; type: string; data: number[] }[];
          period_label: string;
        };
        summary_stats: {
          total_revenue: string;
          processed_transactions: number;
          total_payments: number;
          unprocessed_mpesa_transactions: number;
          currency: string;
          period: string;
          start_date: string;
          end_date: string;
          generated_at: string;
        };
      };
      status: string;
    }>(`${this.baseUrl}/reports/dashboard/payment-processing-report/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Dashboard Methods
  getDashboardComplete(limit: number = 10): Observable<CompleteDashboard> {
    return this.http.get<CompleteDashboard>(`${this.baseUrl}/reports/dashboard/complete/?transaction_limit=${limit}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getDashboardSummary(): Observable<DashboardSummary> {
    return this.http.get<DashboardSummary>(`${this.baseUrl}/reports/dashboard/summary/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // DeliverFasta Dashboard Methods (based on REPORTS_SPECIFICATION.md)
  /**
   * Get dashboard summary statistics
   * Endpoint: GET /api/dashboard/summary/
   */
  getDashboardSummaryStatistics(params?: { 
    period?: string;
    hub_id?: string;
    company_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<DashboardSummaryStatistics> {
    let url = `${this.baseUrl}/dashboard/summary/`;
    const queryParams: string[] = [];
    
    if (params?.period) {
      queryParams.push(`period=${params.period}`);
    }
    if (params?.hub_id) {
      queryParams.push(`hub_id=${params.hub_id}`);
    }
    if (params?.company_id) {
      queryParams.push(`company_id=${params.company_id}`);
    }
    if (params?.start_date) {
      queryParams.push(`start_date=${params.start_date}`);
    }
    if (params?.end_date) {
      queryParams.push(`end_date=${params.end_date}`);
    }
    if (params?.start_time) {
      queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
    }
    if (params?.end_time) {
      queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
    }
    
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    return this.http.get<DashboardSummaryStatistics>(url, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Get sales & revenue statistics chart data
   * Endpoint: GET /api/dashboard/sales-revenue-chart/
   */
  getSalesRevenueChart(params: { 
    period?: string;
    hub_id?: string;
    company_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<SalesRevenueChartResponse> {
    const queryParams: string[] = [];
    
    // Add period if provided, otherwise use date range
    if (params.period) {
      queryParams.push(`period=${params.period}`);
    }
    if (params.start_date) {
      queryParams.push(`start_date=${params.start_date}`);
    }
    if (params.end_date) {
      queryParams.push(`end_date=${params.end_date}`);
    }
    if (params.start_time) {
      queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
    }
    if (params.end_time) {
      queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
    }
    if (params.hub_id) {
      queryParams.push(`hub_id=${params.hub_id}`);
    }
    if (params.company_id) {
      queryParams.push(`company_id=${params.company_id}`);
    }
    
    return this.http.get<SalesRevenueChartResponse>(`${this.baseUrl}/dashboard/sales-revenue-chart/?${queryParams.join('&')}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Get top performing retailers chart data
   * Endpoint: GET /api/dashboard/top-retailers-chart/
   */
  getTopRetailersChart(params: { 
    period?: string; 
    limit?: number;
    hub_id?: string;
    company_id?: string;
    start_date?: string;
    end_date?: string;
  }): Observable<TopRetailersChartResponse> {
    const queryParams: string[] = [];
    
    // Add period if provided, otherwise use date range
    if (params.period) {
      queryParams.push(`period=${params.period}`);
    }
    if (params.start_date) {
      queryParams.push(`start_date=${params.start_date}`);
    }
    if (params.end_date) {
      queryParams.push(`end_date=${params.end_date}`);
    }
    if (params.limit) {
      queryParams.push(`limit=${params.limit}`);
    }
    if (params.hub_id) {
      queryParams.push(`hub_id=${params.hub_id}`);
    }
    if (params.company_id) {
      queryParams.push(`company_id=${params.company_id}`);
    }
    
    return this.http.get<TopRetailersChartResponse>(`${this.baseUrl}/dashboard/top-retailers-chart/?${queryParams.join('&')}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Get orders status chart data
   * Endpoint: GET /api/dashboard/orders-status-chart/
   */
  getOrdersStatusChart(params: { 
    period: string;
    hub_id?: string;
    company_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<OrdersStatusChartResponse> {
    const queryParams: string[] = [`period=${params.period}`];
    
    if (params.hub_id) {
      queryParams.push(`hub_id=${params.hub_id}`);
    }
    if (params.company_id) {
      queryParams.push(`company_id=${params.company_id}`);
    }
    if (params.start_date) {
      queryParams.push(`start_date=${params.start_date}`);
    }
    if (params.end_date) {
      queryParams.push(`end_date=${params.end_date}`);
    }
    if (params.start_time) {
      queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
    }
    if (params.end_time) {
      queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
    }
    
    return this.http.get<OrdersStatusChartResponse>(`${this.baseUrl}/dashboard/orders-status-chart/?${queryParams.join('&')}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Bales summary chart (sold vs pending delivery).
   * Endpoint: GET /api/dashboard/bales-summary/
   */
  getBalesSummary(params: {
    period?: string;
    hub_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
  }): Observable<BalesSummaryResponse> {
    const queryParams: string[] = [];

    if (params.period) {
      queryParams.push(`period=${params.period}`);
    }
    if (params.hub_id) {
      queryParams.push(`hub_id=${params.hub_id}`);
    }
    if (params.start_date) {
      queryParams.push(`start_date=${params.start_date}`);
    }
    if (params.end_date) {
      queryParams.push(`end_date=${params.end_date}`);
    }
    if (params.start_time) {
      queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
    }
    if (params.end_time) {
      queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
    }

    const qs = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.http.get<BalesSummaryResponse>(`${this.baseUrl}/dashboard/bales-summary/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Dashboard Products Report – products and bundles sold.
   * Endpoint: GET /api/dashboard/products_report/
   */
  getDashboardProductsReport(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    hub_id?: string;
    product_type?: 'maize' | 'baking_flour' | 'all';
    unit_type?: 'bundle' | 'all';
    page?: number;
    page_size?: number;
  }): Observable<DashboardProductsReportResponse> {
    const queryParams: string[] = [];
    if (params) {
      if (params.period) queryParams.push(`period=${encodeURIComponent(params.period)}`);
      if (params.start_date) queryParams.push(`start_date=${params.start_date}`);
      if (params.end_date) queryParams.push(`end_date=${params.end_date}`);
      if (params.start_time) queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
      if (params.end_time) queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
      if (params.hub_id && params.hub_id !== 'all') queryParams.push(`hub_id=${encodeURIComponent(params.hub_id)}`);
      if (params.product_type && params.product_type !== 'all') queryParams.push(`product_type=${params.product_type}`);
      if (params.unit_type && params.unit_type !== 'all') queryParams.push(`unit_type=${params.unit_type}`);
      if (params.page != null) queryParams.push(`page=${params.page}`);
      if (params.page_size != null) queryParams.push(`page_size=${params.page_size}`);
    }
    const url = `${this.baseUrl}/dashboard/products_report/${queryParams.length ? '?' + queryParams.join('&') : ''}`;
    return this.http.get<DashboardProductsReportResponse>(url, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * Top Performing Drivers – GET /api/dashboard/top-performing-drivers/
   * Params: period, start_date, end_date, hub_id, limit (default 10, max 100).
   */
  getTopPerformingDrivers(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    hub_id?: string;
    limit?: number;
  }): Observable<TopPerformingDriversResponse> {
    const queryParams: string[] = [];
    if (params) {
      if (params.period) queryParams.push(`period=${encodeURIComponent(params.period)}`);
      if (params.start_date) queryParams.push(`start_date=${params.start_date}`);
      if (params.end_date) queryParams.push(`end_date=${params.end_date}`);
      if (params.start_time) queryParams.push(`start_time=${encodeURIComponent(params.start_time)}`);
      if (params.end_time) queryParams.push(`end_time=${encodeURIComponent(params.end_time)}`);
      if (params.hub_id && params.hub_id !== 'all') queryParams.push(`hub_id=${encodeURIComponent(params.hub_id)}`);
      if (params.limit != null) queryParams.push(`limit=${params.limit}`);
    }
    const url = `${this.baseUrl}/dashboard/top-performing-drivers/${queryParams.length ? '?' + queryParams.join('&') : ''}`;
    return this.http.get<TopPerformingDriversResponse>(url, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentStatusDistribution(): Observable<PaymentStatusDistribution> {
    return this.http.get<PaymentStatusDistribution>(`${this.baseUrl}/reports/dashboard/payment-status/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getMonthlyPaymentTrends(): Observable<MonthlyPaymentTrends> {
    return this.http.get<MonthlyPaymentTrends>(`${this.baseUrl}/reports/dashboard/monthly-trends/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getTopPaymentGateways(): Observable<TopPaymentGateways> {
    return this.http.get<TopPaymentGateways>(`${this.baseUrl}/reports/dashboard/top-gateways/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getRecentTransactions(limit: number = 10): Observable<RecentTransactions> {
    return this.http.get<RecentTransactions>(`${this.baseUrl}/reports/dashboard/recent-transactions/?transaction_limit=${limit}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Gateway CRUD Operations
  getGateways(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    status?: string,
    gateway_type?: string,
    company?: string,
    ordering?: string 
  }): Observable<GatewayListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<GatewayListResponse>(`${this.baseUrl}/gateways/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getGateway(id: string): Observable<Gateway> {
    return this.http.get<Gateway>(`${this.baseUrl}/gateways/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createGateway(gateway: CreateGatewayRequest): Observable<Gateway> {
    return this.http.post<Gateway>(`${this.baseUrl}/gateways/`, gateway, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateGateway(id: string, gateway: Partial<Gateway>): Observable<Gateway> {
    return this.http.put<Gateway>(`${this.baseUrl}/gateways/${id}/`, gateway, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteGateway(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/gateways/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Gateway Detail Operations
  getGatewayDetails(params?: { 
    page?: number, 
    page_size?: number, 
    gateway?: string,
    status?: string 
  }): Observable<{ count: number; results: GatewayDetail[] }> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<{ count: number; results: GatewayDetail[] }>(`${this.baseUrl}/gateway_details/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getGatewayDetail(id: string): Observable<GatewayDetail> {
    return this.http.get<GatewayDetail>(`${this.baseUrl}/gateway_details/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  updateGatewayDetail(id: string, gatewayDetail: Partial<GatewayDetail>): Observable<GatewayDetail> {
    return this.http.put<GatewayDetail>(`${this.baseUrl}/gateway_details/${id}/`, gatewayDetail, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Combined gateway creation method
  createCombinedGateway(data: CreateCombinedGatewayRequest): Observable<Gateway> {
    return new Observable(observer => {
      // First create the gateway
      const gatewayData: CreateGatewayRequest = {
        name: data.name,
        gateway_type: data.gateway_type,
        company: data.company,
        branch: data.branch,
        mpesa_api: data.mpesa_api || false,
        pull_api: data.pull_api || false
      };

      this.createGateway(gatewayData).subscribe({
        next: (gateway) => {
          // Then create the gateway detail if any detail fields are provided
          const hasDetailFields = Boolean(
            data.api_key || data.ho_no || data.store_no || data.consumer_key || 
            data.consumer_secret || data.initiator_name || data.initiator_password || 
            data.pass_key || data.identifier || data.mpesa_type || data.admin_contact || 
            data.callback_one || data.callback_two || data.country || data.currency || 
            data.version
          );

          if (hasDetailFields) {
            const detailData: CreateGatewayDetailRequest = {
              gateway: gateway.id,
              api_key: data.api_key,
              ho_no: data.ho_no,
              store_no: data.store_no,
              consumer_key: data.consumer_key,
              consumer_secret: data.consumer_secret,
              initiator_name: data.initiator_name,
              initiator_password: data.initiator_password,
              pass_key: data.pass_key,
              identifier: data.identifier,
              mpesa_type: data.mpesa_type,
              admin_contact: data.admin_contact,
              callback_one: data.callback_one,
              callback_two: data.callback_two,
              country: data.country,
              currency: data.currency,
              version: data.version
            };

            this.http.post<GatewayDetail>(`${this.baseUrl}/gateway_details/`, detailData, {
              headers: this.getAuthHeaders()
            }).subscribe({
              next: (detail) => {
                // Return the gateway with detail populated
                gateway.gateway_detail = detail;
                observer.next(gateway);
                observer.complete();
              },
              error: (error) => {
                // If detail creation fails, we might want to delete the gateway
                // or just return the gateway without details
                console.warn('Gateway detail creation failed:', error);
                observer.next(gateway);
                observer.complete();
              }
            });
          } else {
            // No detail fields provided, just return the gateway
            observer.next(gateway);
            observer.complete();
          }
        },
        error: (error) => {
          observer.error(error);
        }
      });
    });
  }

  // Pages CRUD Operations
  getPages(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    name?: string,
    ordering?: string 
  }): Observable<PageListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PageListResponse>(`${this.baseUrl}/pages/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPage(id: number): Observable<Page> {
    return this.http.get<Page>(`${this.baseUrl}/pages/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createPage(page: CreatePageRequest): Observable<Page> {
    return this.http.post<Page>(`${this.baseUrl}/pages/`, page, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updatePage(id: number, page: Partial<CreatePageRequest>): Observable<Page> {
    return this.http.put<Page>(`${this.baseUrl}/pages/${id}/`, page, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deletePage(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/pages/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // User CRUD Operations
  getUsers(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    role?: string | number,
    role_name?: string,
    is_active?: boolean,
    is_offboarded?: boolean,
    ordering?: string 
  }): Observable<UserListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<UserListResponse>(`${this.baseUrl}/users/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUser(id: string): Observable<User> {
    return this.http.get<User>(`${this.baseUrl}/users/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * GET /api/users/me/ – current authenticated user details.
   */
  getMe(): Observable<UserMeResponse> {
    return this.http.get<UserMeResponse>(`${this.baseUrl}/users/me/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /**
   * POST /api/users/me/change-password/ – change current user password.
   * Body: { current_password, new_password, confirm_password }.
   */
  changePasswordMe(body: ChangePasswordMeRequest): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/users/me/change-password/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * PUT/POST /api/users/me/image/ – update current user's profile image only.
   * multipart/form-data with field "image" (jpg, jpeg, png).
   */
  updateMeImage(file: File): Observable<{ message: string; image: string }> {
    const formData = new FormData();
    formData.append('image', file, file.name);
    return this.http
      .put<{ message: string; image: string }>(`${this.baseUrl}/users/me/image/`, formData, {
        headers: this.getFormDataHeaders()
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Driver stats: total orders, commission, completed/pending orders, bundles delivered.
   * Params: period (e.g. last_7_days), or start_date + end_date (YYYY-MM-DD).
   */
  getDriverStats(
    userId: string,
    params?: { period?: string; start_date?: string; end_date?: string }
  ): Observable<DriverStatsResponse> {
    const queryParams: string[] = [];
    if (params?.period) queryParams.push(`period=${encodeURIComponent(params.period)}`);
    if (params?.start_date) queryParams.push(`start_date=${params.start_date}`);
    if (params?.end_date) queryParams.push(`end_date=${params.end_date}`);
    const qs = queryParams.length ? `?${queryParams.join('&')}` : '';
    return this.http.get<DriverStatsResponse>(
      `${this.baseUrl}/users/${userId}/driver-stats/${qs}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  createUser(user: CreateUserRequest): Observable<User> {
    // If there's an image file, use FormData, otherwise use JSON
    if (user.image && user.image instanceof File) {
      const formData = new FormData();
      
      Object.entries(user).forEach(([key, value]) => {
        if (key === 'image' && value instanceof File) {
          formData.append('image', value);
        } else if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value.toString());
        }
      });

      return this.http.post<User>(`${this.baseUrl}/users/`, formData, {
        headers: this.getFormDataHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    } else {
      // Send as JSON - remove undefined, null, and empty string values
      const jsonPayload: any = {};
      Object.entries(user).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          jsonPayload[key] = value;
        }
      });
      
      return this.http.post<User>(`${this.baseUrl}/users/`, jsonPayload, {
        headers: this.getAuthHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    }
  }

  /** GET /api/staff/roles/ – suggested staff role names. */
  getStaffRoles(): Observable<{ roles: string[] }> {
    return this.http.get<{ roles: string[] }>(`${this.baseUrl}/staff/roles/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** GET /api/staff/ – list staff (User + StaffProfile). */
  getStaff(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    branch_id?: string;
    is_active?: boolean;
  }): Observable<StaffListResponse | StaffMember[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<StaffListResponse | StaffMember[]>(`${this.baseUrl}/staff/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/staff/ – register staff with compensation (payroll-ready). */
  createStaff(payload: CreateStaffRequest): Observable<StaffMember> {
    const body: Record<string, unknown> = {};
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        body[key] = value;
      }
    });
    return this.http.post<StaffMember>(`${this.baseUrl}/staff/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** GET /api/departments/?branch=&is_active=true */
  getDepartments(params?: {
    branch?: string;
    branch_id?: string;
    is_active?: boolean;
    page_size?: number;
    page?: number;
    search?: string;
  }): Observable<DepartmentListResponse | Department[]> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<DepartmentListResponse | Department[]>(`${this.baseUrl}/departments/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createDriver(driver: CreateUserRequest & { hub?: string; branch?: string }): Observable<User> {
    const jsonPayload: Record<string, unknown> = {};
    Object.entries(driver).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        jsonPayload[key] = value;
      }
    });
    if (jsonPayload['hub'] && !jsonPayload['branch']) {
      jsonPayload['branch'] = jsonPayload['hub'];
    }
    delete jsonPayload['hub'];

    return this.http.post<User>(`${this.baseUrl}/users/create-driver/`, jsonPayload, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateUser(id: string, user: Partial<CreateUserRequest>): Observable<User> {
    const formData = new FormData();
    
    Object.entries(user).forEach(([key, value]) => {
      if (key === 'image' && value instanceof File) {
        formData.append('image', value);
      } else if (
        ['bank_branch', 'bank_code', 'county', 'postal_code', 'kin_email', 'allowances'].includes(key)
        && value !== undefined
        && value !== null
      ) {
        formData.append(key, value.toString());
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }
    });

    return this.http.put<User>(`${this.baseUrl}/users/${id}/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/users/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /** GET disciplinary actions for a user. */
  getDisciplinaryActions(userId: string): Observable<DisciplinaryAction[]> {
    return this.http.get<DisciplinaryAction[]>(`${this.baseUrl}/users/${userId}/disciplinary-actions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** GET /api/users/{id}/compensation/ — current pay and history. */
  getUserCompensation(userId: string): Observable<StaffCompensationResponse> {
    return this.http.get<StaffCompensationResponse>(`${this.baseUrl}/users/${userId}/compensation/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  /** POST /api/users/{id}/compensation/ — update pay and append history. */
  updateUserCompensation(userId: string, payload: UpdateStaffCompensationRequest): Observable<StaffCompensationResponse> {
    return this.http.post<StaffCompensationResponse>(`${this.baseUrl}/users/${userId}/compensation/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * GET /api/users/me/login-activity/ — sign-in history for the current user.
   */
  getMyLoginActivity(params?: { page?: number; page_size?: number }): Observable<LoginActivity[]> {
    return this.http.get<unknown>(`${this.baseUrl}/users/me/login-activity/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res) => this.normalizeLoginActivityList(res)),
      catchError(this.handleError)
    );
  }

  /**
   * GET /api/users/{id}/login-activity/ — sign-in history for a user (admin).
   */
  getUserLoginActivity(userId: string, params?: { page?: number; page_size?: number }): Observable<LoginActivity[]> {
    return this.http.get<unknown>(`${this.baseUrl}/users/${userId}/login-activity/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res) => this.normalizeLoginActivityList(res)),
      catchError(this.handleError)
    );
  }

  /** PATCH user status (activate/deactivate). For deactivation, pass reason and category. */
  updateUserStatus(userId: string, payload: UpdateUserStatusRequest): Observable<User> {
    return this.http.patch<User>(`${this.baseUrl}/users/${userId}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /** POST /api/users/{id}/offboard/ — revoke login; keep reports through last working day. */
  offboardUser(userId: string, payload: OffboardUserRequest): Observable<User> {
    return this.http.post<User>(`${this.baseUrl}/users/${userId}/offboard/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Role CRUD Operations
  getRoles(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    ordering?: string 
  }): Observable<RoleListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<RoleListResponse>(`${this.baseUrl}/roles/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getRole(id: string | number): Observable<Role> {
    return this.http.get<Role>(`${this.baseUrl}/roles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getRetailerRequirements(): Observable<RetailerRequirementsResponse> {
    return this.http.get<RetailerRequirementsResponse>(`${this.baseUrl}/roles/retailer/requirements/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createRole(role: CreateRoleRequest): Observable<Role> {
    return this.http.post<Role>(`${this.baseUrl}/roles/`, role, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateRole(id: string | number, role: Partial<CreateRoleRequest>): Observable<Role> {
    return this.http.put<Role>(`${this.baseUrl}/roles/${id}/`, role, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteRole(id: string | number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/roles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // User Groups CRUD Operations
  getUserGroups(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    ordering?: string 
  }): Observable<UserGroupListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<UserGroupListResponse>(`${this.baseUrl}/usergroups/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUserGroup(id: number): Observable<UserGroup> {
    return this.http.get<UserGroup>(`${this.baseUrl}/usergroups/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUserGroupDetail(id: number): Observable<UserGroupDetail> {
    return this.http.get<UserGroupDetail>(`${this.baseUrl}/group_details/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createUserGroup(group: CreateUserGroupRequest): Observable<UserGroup> {
    return this.http.post<UserGroup>(`${this.baseUrl}/usergroups/`, group, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateUserGroup(id: number, group: Partial<CreateUserGroupRequest>): Observable<UserGroup> {
    return this.http.put<UserGroup>(`${this.baseUrl}/usergroups/${id}/`, group, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteUserGroup(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/usergroups/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // User Group Rights CRUD Operations
  getUserGroupRights(params?: { 
    page?: number, 
    page_size?: number, 
    group?: number,
    page_id?: number,
    ordering?: string 
  }): Observable<UserGroupRightsListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<UserGroupRightsListResponse>(`${this.baseUrl}/group_rights/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUserGroupRight(id: number): Observable<UserGroupRights> {
    return this.http.get<UserGroupRights>(`${this.baseUrl}/group_rights/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createUserGroupRight(rights: CreateUserGroupRightsRequest): Observable<UserGroupRights> {
    return this.http.post<UserGroupRights>(`${this.baseUrl}/group_rights/`, rights, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateUserGroupRight(id: number, rights: Partial<CreateUserGroupRightsRequest>): Observable<UserGroupRights> {
    return this.http.put<UserGroupRights>(`${this.baseUrl}/group_rights/${id}/`, rights, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteUserGroupRight(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/group_rights/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // User Group Members CRUD Operations
  getUserGroupMembers(params?: { 
    page?: number, 
    page_size?: number, 
    user?: string,
    group?: number,
    active?: number,
    ordering?: string 
  }): Observable<UserGroupMemberListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<UserGroupMemberListResponse>(`${this.baseUrl}/group_members/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUserGroupMember(id: number): Observable<UserGroupMember> {
    return this.http.get<UserGroupMember>(`${this.baseUrl}/group_members/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createUserGroupMember(member: CreateUserGroupMemberRequest): Observable<UserGroupMember> {
    return this.http.post<UserGroupMember>(`${this.baseUrl}/group_members/`, member, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateUserGroupMember(id: number, member: Partial<CreateUserGroupMemberRequest>): Observable<UserGroupMember> {
    return this.http.put<UserGroupMember>(`${this.baseUrl}/group_members/${id}/`, member, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteUserGroupMember(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/group_members/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Enhanced User Details
  getUserDetails(id: string): Observable<UserDetails> {
    return this.http.get<UserDetails>(`${this.baseUrl}/user-details/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getAllUserDetails(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    role?: number,
    ordering?: string 
  }): Observable<{count: number, next?: string, previous?: string, results: UserDetails[]}> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<{count: number, next?: string, previous?: string, results: UserDetails[]}>(`${this.baseUrl}/user-details/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }



  // Payment Request CRUD Operations
  getPaymentRequests(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    status?: string,
    payment_method?: string,
    company?: string,
    gateway?: string,
    ordering?: string 
  }): Observable<PaymentRequestListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PaymentRequestListResponse>(`${this.baseUrl}/payment_requests/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentRequest(id: string): Observable<PaymentRequest> {
    return this.http.get<PaymentRequest>(`${this.baseUrl}/payment_requests/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createPaymentRequest(paymentRequest: CreatePaymentRequestRequest): Observable<PaymentRequest> {
    return this.http.post<PaymentRequest>(`${this.baseUrl}/payment_requests/`, paymentRequest, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updatePaymentRequest(id: string, paymentRequest: Partial<CreatePaymentRequestRequest>): Observable<PaymentRequest> {
    return this.http.put<PaymentRequest>(`${this.baseUrl}/payment_requests/${id}/`, paymentRequest, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deletePaymentRequest(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payment_requests/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Payment Request specific endpoints
  getPendingPaymentRequests(): Observable<PaymentRequest[]> {
    return this.http.get<PaymentRequest[]>(`${this.baseUrl}/payment_requests/pending_requests/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompletedPaymentRequests(): Observable<PaymentRequest[]> {
    return this.http.get<PaymentRequest[]>(`${this.baseUrl}/payment_requests/completed_requests/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getExpiredPaymentRequests(): Observable<PaymentRequest[]> {
    return this.http.get<PaymentRequest[]>(`${this.baseUrl}/payment_requests/expired_requests/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentRequestSummary(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/payment_requests/request_summary/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  approvePaymentRequest(id: string): Observable<PaymentRequest> {
    return this.http.post<PaymentRequest>(`${this.baseUrl}/payment_requests/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  rejectPaymentRequest(id: string): Observable<PaymentRequest> {
    return this.http.post<PaymentRequest>(`${this.baseUrl}/payment_requests/${id}/reject/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Payment Transaction CRUD Operations
  getPaymentTransactions(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    status?: string,
    currency?: string,
    payment_request?: string,
    ordering?: string,
    gateway?: string,
    [key: string]: any
  }): Observable<PaymentTransactionListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PaymentTransactionListResponse>(`${this.baseUrl}/payment_transactions/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentTransaction(id: string): Observable<PaymentTransaction> {
    return this.http.get<PaymentTransaction>(`${this.baseUrl}/payment_transactions/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Payment Refund CRUD Operations
  getPaymentRefunds(params?: { 
    page?: number, 
    page_size?: number, 
    search?: string, 
    status?: string,
    transaction?: string,
    ordering?: string 
  }): Observable<PaymentRefundListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PaymentRefundListResponse>(`${this.baseUrl}/payment_refunds/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentRefund(id: string): Observable<PaymentRefund> {
    return this.http.get<PaymentRefund>(`${this.baseUrl}/payment_refunds/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createPaymentRefund(refund: CreatePaymentRefundRequest): Observable<PaymentRefund> {
    return this.http.post<PaymentRefund>(`${this.baseUrl}/payment_refunds/`, refund, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updatePaymentRefund(id: string, refund: Partial<CreatePaymentRefundRequest>): Observable<PaymentRefund> {
    return this.http.put<PaymentRefund>(`${this.baseUrl}/payment_refunds/${id}/`, refund, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deletePaymentRefund(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payment_refunds/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Payment Refund specific endpoints
  getPendingRefunds(): Observable<PaymentRefund[]> {
    return this.http.get<PaymentRefund[]>(`${this.baseUrl}/payment_refunds/pending_refunds/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCompletedRefunds(): Observable<PaymentRefund[]> {
    return this.http.get<PaymentRefund[]>(`${this.baseUrl}/payment_refunds/completed_refunds/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getRefundSummary(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/payment_refunds/refund_summary/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Mpesa Transactions Search (by transactionId via search param)
  searchMpesaTransactionsByTransactionId(transactionId: string, limit: number = 10, additionalParams?: any): Observable<MpesaTransactionListResponse> {
    const params = new URLSearchParams();
    params.append('search', transactionId);
    params.append('page_size', String(limit));
    
    // Add additional filter parameters
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        if (additionalParams[key] !== null && additionalParams[key] !== undefined && additionalParams[key] !== '') {
          params.append(key, String(additionalParams[key]));
        }
      });
    }
    
    return this.http.get<MpesaTransactionListResponse>(`${this.baseUrl}/mpesa_transactions/?${params.toString()}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // STK Logs API methods
  getStkLogs(page: number = 1, pageSize: number = 10, search?: string, additionalParams?: any): Observable<StkLogsResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (search) {
      params.append('search', search);
    }
    
    // Add additional filter parameters
    if (additionalParams) {
      Object.keys(additionalParams).forEach(key => {
        if (additionalParams[key] !== null && additionalParams[key] !== undefined && additionalParams[key] !== '') {
          params.append(key, String(additionalParams[key]));
        }
      });
    }
    
    return this.http.get<StkLogsResponse>(`${this.baseUrl}/stk_logs/?${params.toString()}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getStkLogById(id: string): Observable<StkLog> {
    return this.http.get<StkLog>(`${this.baseUrl}/stk_logs/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Income Report API
  getIncomeReport(params?: { 
    company?: string,
    start_date?: string,
    end_date?: string,
    period?: string
  }): Observable<{
    total_revenue: number;
    total_transactions: number;
    success_rate: number;
    growth_percentage: number;
    period: string;
    generated_at: string;
  }> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<{
      total_revenue: number;
      total_transactions: number;
      success_rate: number;
      growth_percentage: number;
      period: string;
      generated_at: string;
    }>(`${this.baseUrl}/reports/income-report/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Additional Permissions System Methods
  getNavigation(): Observable<NavigationResponse> {
    return this.http.get<NavigationResponse>(`${this.baseUrl}/pages/navigation/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getDashboardWidgets(): Observable<DashboardWidgetsResponse> {
    return this.http.get<DashboardWidgetsResponse>(`${this.baseUrl}/pages/dashboard-widgets/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getUserPermissionsResponse(): Observable<UserPermissionsResponse> {
    return this.http.get<UserPermissionsResponse>(`${this.baseUrl}/pages/user-permissions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  checkPagePermission(id: string, permission: string): Observable<CheckPermissionResponse> {
    return this.http.get<CheckPermissionResponse>(`${this.baseUrl}/pages/${id}/check-permission/?permission=${permission}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getRolePermissions(id: string | number): Observable<RolePermissionsMatrix> {
    return this.http.get<RolePermissionsMatrix>(`${this.baseUrl}/roles/${id}/permissions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  updateRolePermissions(id: string | number, permissions: Array<{
    module: string;
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
  }>): Observable<RolePermissionsMatrix> {
    return this.http.put<RolePermissionsMatrix>(`${this.baseUrl}/roles/${id}/permissions/`, { permissions }, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getMyPermissions(): Observable<MyPermissionsResponse> {
    return this.http.get<MyPermissionsResponse>(`${this.baseUrl}/users/me/permissions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  addRolePermission(id: string | number, permissionData: AddRolePermissionRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/roles/${id}/add-permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  removeRolePermission(id: string | number, permissionData: RemoveRolePermissionRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/roles/${id}/remove-permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getGroupPermissions(id: number): Observable<GroupPermissionsResponse> {
    return this.http.get<GroupPermissionsResponse>(`${this.baseUrl}/usergroups/${id}/permissions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  addGroupPermission(id: number, permissionData: AddGroupPermissionRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usergroups/${id}/add-permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  removeGroupPermission(id: number, permissionData: RemoveGroupPermissionRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/usergroups/${id}/remove-permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getUserPermissionsByUserId(userId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/users/${userId}/permissions/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  checkUserPermission(userId: string, permissionData: CheckPermissionRequest): Observable<CheckPermissionResponse> {
    return this.http.post<CheckPermissionResponse>(`${this.baseUrl}/users/${userId}/check-permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  // Overloaded methods for backward compatibility
  addUserToGroup(userId: string, groupId: number): Observable<any>;
  addUserToGroup(membershipData: AddUserToGroupRequest): Observable<any>;
  addUserToGroup(userIdOrData: string | AddUserToGroupRequest, groupId?: number): Observable<any> {
    if (typeof userIdOrData === 'string' && groupId !== undefined) {
      // Old signature: addUserToGroup(userId: string, groupId: number)
      const membershipData: AddUserToGroupRequest = {
        user_id: userIdOrData,
        group_id: groupId
      };
      return this.http.post<any>(`${this.baseUrl}/add_user_to_group/`, membershipData, {
        headers: this.getAuthHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    } else {
      // New signature: addUserToGroup(membershipData: AddUserToGroupRequest)
      return this.http.post<any>(`${this.baseUrl}/add_user_to_group/`, userIdOrData, {
        headers: this.getAuthHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    }
  }

  removeUserFromGroup(userId: string, groupId: number): Observable<any>;
  removeUserFromGroup(membershipData: RemoveUserFromGroupRequest): Observable<any>;
  removeUserFromGroup(userIdOrData: string | RemoveUserFromGroupRequest, groupId?: number): Observable<any> {
    if (typeof userIdOrData === 'string' && groupId !== undefined) {
      // Old signature: removeUserFromGroup(userId: string, groupId: number)
      const membershipData: RemoveUserFromGroupRequest = {
        user_id: userIdOrData,
        group_id: groupId
      };
      return this.http.post<any>(`${this.baseUrl}/remove_user_from_group/`, membershipData, {
        headers: this.getAuthHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    } else {
      // New signature: removeUserFromGroup(membershipData: RemoveUserFromGroupRequest)
      return this.http.post<any>(`${this.baseUrl}/remove_user_from_group/`, userIdOrData, {
        headers: this.getAuthHeaders()
      }).pipe(
        catchError(this.handleError)
      );
    }
  }

  bulkAddUsersToGroup(bulkData: BulkAddUsersToGroupRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/bulk_add_users_to_group/`, bulkData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  bulkRemoveUsersFromGroup(bulkData: BulkRemoveUsersFromGroupRequest): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/bulk_remove_users_from_group/`, bulkData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  checkPermission(permissionData: CheckPermissionRequest): Observable<CheckPermissionResponse> {
    return this.http.post<CheckPermissionResponse>(`${this.baseUrl}/check_permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  addGroupPermissionDirect(permissionData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/add_group_permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  removeGroupPermissionDirect(permissionData: any): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/remove_group_permission/`, permissionData, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Vehicle Management Operations
  
  // Vehicle Operations
  getVehicles(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    type?: string,
    color?: string,
    registration_number?: string,
    driver?: string,
    status?: string,
    ordering?: string
  }): Observable<VehicleListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<VehicleListResponse>(`${this.baseUrl}/vehicles/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getVehicle(id: string): Observable<Vehicle> {
    return this.http.get<Vehicle>(`${this.baseUrl}/vehicles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createVehicle(vehicle: CreateVehicleRequest): Observable<Vehicle> {
    if (vehicle.image instanceof File) {
      return this.http.post<Vehicle>(`${this.baseUrl}/vehicles/`, this.toVehicleFormData(vehicle), {
        headers: this.getFormDataHeaders()
      }).pipe(catchError(this.handleError));
    }
    const { image, ...payload } = vehicle;
    return this.http.post<Vehicle>(`${this.baseUrl}/vehicles/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateVehicle(id: string, vehicle: Partial<CreateVehicleRequest>): Observable<Vehicle> {
    if (vehicle.image instanceof File) {
      return this.http.patch<Vehicle>(`${this.baseUrl}/vehicles/${id}/`, this.toVehicleFormData(vehicle), {
        headers: this.getFormDataHeaders()
      }).pipe(catchError(this.handleError));
    }
    const { image, ...payload } = vehicle;
    return this.http.patch<Vehicle>(`${this.baseUrl}/vehicles/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  private toVehicleFormData(vehicle: Partial<CreateVehicleRequest>): FormData {
    const formData = new FormData();
    Object.entries(vehicle).forEach(([key, value]) => {
      if (key === 'image' && value instanceof File) {
        formData.append('image', value);
      } else if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    });
    return formData;
  }

  deleteVehicle(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/vehicles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  assignDriverToVehicle(vehicleId: string, driverId: string, notes?: string): Observable<AssignDriverToVehicleResponse> {
    const body: { driver_id: string; notes?: string } = { driver_id: driverId };
    if (notes?.trim()) {
      body.notes = notes.trim();
    }
    return this.http.post<AssignDriverToVehicleResponse>(
      `${this.baseUrl}/vehicles/${vehicleId}/assign_driver/`,
      body,
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError(this.handleError)
    );
  }

  unassignDriverFromVehicle(vehicleId: string): Observable<UnassignDriverFromVehicleResponse> {
    return this.http.post<UnassignDriverFromVehicleResponse>(
      `${this.baseUrl}/vehicles/${vehicleId}/unassign_driver/`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError(this.handleError)
    );
  }

  // Vehicle Type Operations
  getVehicleTypes(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    ordering?: string
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_types/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Driver Assignment Operations
  getDriverAssignments(params?: {
    page?: number,
    page_size?: number,
    vehicle?: string,
    driver?: string,
    is_active?: boolean,
    ordering?: string
  }): Observable<VehicleDriverAssignmentListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }

    return this.http.get<VehicleDriverAssignmentListResponse>(
      `${this.baseUrl}/vehicle_driver_assignments/${httpParams}`,
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError(this.handleError)
    );
  }

  getDriverReport(driverId: string, startDate?: string, endDate?: string): Observable<any> {
    let httpParams = new URLSearchParams();
    httpParams.append('driver_id', driverId);
    if (startDate) httpParams.append('start_date', startDate);
    if (endDate) httpParams.append('end_date', endDate);
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_driver_assignments/driver_report/?${httpParams.toString()}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getVehicleReport(vehicleId: string, startDate?: string, endDate?: string): Observable<any> {
    let httpParams = new URLSearchParams();
    httpParams.append('vehicle_id', vehicleId);
    if (startDate) httpParams.append('start_date', startDate);
    if (endDate) httpParams.append('end_date', endDate);
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_driver_assignments/vehicle_report/?${httpParams.toString()}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getAssignmentPeriodReport(assignmentId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/vehicle_driver_assignments/${assignmentId}/assignment_period_report/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Maintenance Operations
  getMaintenanceTypes(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    ordering?: string
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/maintenance_types/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createMaintenanceType(maintenanceType: {
    name: string;
    description?: string;
    default_interval_km?: number;
    default_interval_days?: number;
    estimated_cost?: number;
  }): Observable<MaintenanceType> {
    return this.http.post<MaintenanceType>(`${this.baseUrl}/maintenance_types/`, maintenanceType, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getVehicleMaintenances(params?: {
    page?: number,
    page_size?: number,
    vehicle?: string,
    maintenance_type?: string,
    status?: string,
    ordering?: string
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_maintenances/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createVehicleMaintenance(maintenance: {
    vehicle: string;
    maintenance_type: string;
    scheduled_date: string;
    odometer_reading_at_schedule?: number;
    service_provider?: string;
    description?: string;
    notes?: string;
  }): Observable<VehicleMaintenance> {
    return this.http.post<VehicleMaintenance>(`${this.baseUrl}/vehicle_maintenances/`, maintenance, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  markMaintenanceCompleted(maintenanceId: string, data: {
    odometer_reading_at_completion?: number;
    cost: number;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/vehicle_maintenances/${maintenanceId}/mark_completed/`, data, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getUpcomingMaintenances(params?: {
    days?: number;
    driver_id?: string;
    vehicle_id?: string;
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_maintenances/upcoming/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getMaintenanceStatistics(params?: {
    vehicle_id?: string;
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/vehicle_maintenances/statistics/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Repair Request Operations
  getRepairRequests(params?: {
    page?: number,
    page_size?: number,
    vehicle?: string,
    driver?: string,
    status?: string,
    priority?: string,
    ordering?: string
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/repair_requests/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  createRepairRequest(repair: {
    vehicle: string;
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    reported_location?: string;
    odometer_reading?: number;
    issue_images?: string[];
  }): Observable<RepairRequest> {
    return this.http.post<RepairRequest>(`${this.baseUrl}/repair_requests/`, repair, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  approveRepairRequest(repairId: string, estimatedCost: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/repair_requests/${repairId}/approve/`, {
      estimated_cost: estimatedCost
    }, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  markRepairCompleted(repairId: string, data: {
    actual_cost: number;
    repair_description: string;
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/repair_requests/${repairId}/mark_completed/`, data, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  rejectRepairRequest(repairId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/repair_requests/${repairId}/reject/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getPendingRepairs(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/repair_requests/pending/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  getRepairStatistics(params?: {
    vehicle_id?: string;
  }): Observable<any> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<any>(`${this.baseUrl}/repair_requests/statistics/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  /** Drivers are users with the Driver role. GET /api/users/?role_name=Driver */
  getDrivers(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    is_active?: boolean,
    ordering?: string
  }): Observable<UserListResponse> {
    return this.getUsers({
      page: params?.page,
      page_size: params?.page_size,
      search: params?.search,
      ordering: params?.ordering,
      role_name: 'Driver',
      is_active: params?.is_active,
    });
  }

  // Get retailers
  getRetailers(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    hub?: string,
    is_active?: boolean,
    ordering?: string
  }): Observable<RetailersListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<RetailersListResponse>(`${this.baseUrl}/users/retailers/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Get Payments
  getPayments(params?: {
    page?: number,
    page_size?: number,
    search?: string,
    status_code?: string,
    payment_method_code?: string,
    ordering?: string
  }): Observable<PaymentListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PaymentListResponse>(`${this.baseUrl}/payments/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Payment Methods API
  getPaymentMethods(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    code?: string;
    ordering?: string;
  }): Observable<PaymentMethodListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    
    return this.http.get<PaymentMethodListResponse>(`${this.baseUrl}/payment-methods/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getPaymentMethod(id: string): Observable<PaymentMethod> {
    return this.http.get<PaymentMethod>(`${this.baseUrl}/payment-methods/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createPaymentMethod(paymentMethod: CreatePaymentMethodRequest): Observable<PaymentMethod> {
    return this.http.post<PaymentMethod>(`${this.baseUrl}/payment-methods/`, paymentMethod, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updatePaymentMethod(id: string, paymentMethod: Partial<CreatePaymentMethodRequest>): Observable<PaymentMethod> {
    return this.http.patch<PaymentMethod>(`${this.baseUrl}/payment-methods/${id}/`, paymentMethod, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deletePaymentMethod(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payment-methods/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Commission Tiers (driver commission by day of week)
  getCommissionTiers(params?: {
    day_of_week?: number;
    day_of_week__isnull?: boolean;
    is_active?: boolean;
    search?: string;
    ordering?: string;
    page?: number;
    page_size?: number;
  }): Observable<CommissionTierListResponse> {
    let httpParams = '';
    if (params) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
      httpParams = queryParams.toString() ? `?${queryParams.toString()}` : '';
    }
    return this.http.get<CommissionTierListResponse>(`${this.baseUrl}/commission-tiers/${httpParams}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  getCommissionTier(id: string): Observable<CommissionTier> {
    return this.http.get<CommissionTier>(`${this.baseUrl}/commission-tiers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

  createCommissionTier(body: CreateCommissionTierRequest): Observable<CommissionTier> {
    return this.http.post<CommissionTier>(`${this.baseUrl}/commission-tiers/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  updateCommissionTier(id: string, body: Partial<CreateCommissionTierRequest>): Observable<CommissionTier> {
    return this.http.patch<CommissionTier>(`${this.baseUrl}/commission-tiers/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  deleteCommissionTier(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/commission-tiers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Password Reset APIs
  requestPasswordReset(data: { email?: string; phone?: string }): Observable<{ message: string; expires_in_minutes?: number }> {
    return this.http.post<{ message: string; expires_in_minutes?: number }>(
      `${this.baseUrl}/users/password-reset/request/`,
      data
    ).pipe(
      catchError(this.handleError)
    );
  }

  confirmPasswordReset(data: {
    email?: string;
    phone?: string;
    otp_code: string;
    new_password: string;
    confirm_password: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/users/password-reset/confirm/`,
      data
    ).pipe(
      catchError(this.handleError)
    );
  }

  /** Send password reset email to user. Body: { email }. */
  sendPasswordResetEmail(email: string): Observable<{ message?: string }> {
    return this.http.post<{ message?: string }>(
      `${this.baseUrl}/users/password-reset/send-password/`,
      { email },
      { headers: this.getAuthHeaders() }
    ).pipe(
      catchError(this.handleError)
    );
  }

  // Credit terms (for dropdowns)
  getCreditTerms(params?: { active_only?: boolean }): Observable<CreditTerm[]> {
    const query = params?.active_only === true ? '?active_only=true' : '';
    return this.http.get<CreditTerm[]>(`${this.baseUrl}/credit-terms/${query}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res: any) => {
        if (Array.isArray(res)) return res;
        if (res?.results && Array.isArray(res.results)) return res.results;
        if (res?.data && Array.isArray(res.data)) return res.data;
        return [];
      }),
      catchError(this.handleError)
    );
  }

  getCreditTerm(id: string): Observable<CreditTerm> {
    return this.http.get<CreditTerm>(`${this.baseUrl}/credit-terms/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // Retailer credit accounts
  getRetailerCredits(params?: { retailer_id?: string }): Observable<RetailerCredit[]> {
    const query = params?.retailer_id ? `?retailer_id=${encodeURIComponent(params.retailer_id)}` : '';
    return this.http.get<RetailerCredit[]>(`${this.baseUrl}/retailer-credits/${query}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res: any) => Array.isArray(res) ? res : (res?.results ?? [])),
      catchError(this.handleError)
    );
  }

  getRetailerCredit(id: string): Observable<RetailerCredit> {
    return this.http.get<RetailerCredit>(`${this.baseUrl}/retailer-credits/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  patchRetailerCredit(id: string, body: { credit_limit?: string; credit_terms_id?: string | null; is_active?: boolean }): Observable<RetailerCredit> {
    return this.http.patch<RetailerCredit>(`${this.baseUrl}/retailer-credits/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createRetailerCredit(body: { retailer_id: string; credit_limit?: string; credit_terms_id?: string | null; is_active?: boolean }): Observable<RetailerCredit> {
    return this.http.post<RetailerCredit>(`${this.baseUrl}/retailer-credits/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // Product offers (purchase rewards)
  getProductOffers(params?: { page?: number; page_size?: number; search?: string; is_active?: boolean }): Observable<{ count: number; results: ProductOffer[] }> {
    return this.http.get<any>(`${this.baseUrl}/product-offers/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res: any) => {
        if (Array.isArray(res)) {
          return { count: res.length, results: res };
        }
        const results = res?.results ?? [];
        return { count: res?.count ?? results.length, results };
      }),
      catchError(this.handleError)
    );
  }

  getProductOffer(id: string): Observable<ProductOffer> {
    return this.http.get<ProductOffer>(`${this.baseUrl}/product-offers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createProductOffer(body: {
    name: string;
    product: string;
    quantity_required: string;
    period_months: number;
    reward_description: string;
    is_active?: boolean;
    valid_from?: string | null;
    valid_until?: string | null;
  }): Observable<ProductOffer> {
    return this.http.post<ProductOffer>(`${this.baseUrl}/product-offers/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateProductOffer(id: string, body: Partial<{
    name: string;
    product: string;
    quantity_required: string;
    period_months: number;
    reward_description: string;
    is_active: boolean;
    valid_from: string | null;
    valid_until: string | null;
  }>): Observable<ProductOffer> {
    return this.http.patch<ProductOffer>(`${this.baseUrl}/product-offers/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteProductOffer(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/product-offers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getProductOfferClaims(): Observable<ProductOfferClaim[]> {
    return this.http.get<any>(`${this.baseUrl}/product-offers/claims/`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((res: any) => Array.isArray(res) ? res : (res?.results ?? [])),
      catchError(this.handleError)
    );
  }

  updateProductOfferClaim(id: string, body: { status?: string; notes?: string | null }): Observable<ProductOfferClaim> {
    return this.http.patch<ProductOfferClaim>(`${this.baseUrl}/product-offers/claims/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── GenWatt logistics (stations, fuel orders, trips) ─────────────────────

  getLogisticsDashboardSummary(): Observable<LogisticsDashboardSummary> {
    return this.http.get<LogisticsDashboardSummary>(`${this.baseUrl}/dashboard/summary/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getStations(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    county?: string;
    company_id?: string;
    ordering?: string;
  }): Observable<StationListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<StationListResponse>(`${this.baseUrl}/stations/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getStation(id: string): Observable<Station> {
    return this.http.get<Station>(`${this.baseUrl}/stations/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createStation(body: CreateStationRequest): Observable<Station> {
    return this.http.post<Station>(`${this.baseUrl}/stations/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateStation(id: string, body: UpdateStationRequest): Observable<Station> {
    return this.http.patch<Station>(`${this.baseUrl}/stations/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteStation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/stations/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getFuelOrders(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    station_id?: string;
    branch_id?: string;
    station?: string;
    status?: string;
    status_code?: string;
    vehicle_id?: string;
    trip_id?: string;
    driver_id?: string;
    fuel_type?: string;
    date_from?: string;
    date_to?: string;
    payment_status?: string;
    ordering?: string;
  }): Observable<FuelOrderListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<FuelOrderListResponse>(`${this.baseUrl}/fuel-orders/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getFuelOrdersSummary(params?: {
    station_id?: string;
    branch_id?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    status?: string;
    status_code?: string;
    payment_status?: string;
  }): Observable<FuelOrdersSummary> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<FuelOrdersSummary>(`${this.baseUrl}/fuel-orders/summary/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getTrips(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    route_id?: string;
    prime_mover_id?: string;
    trailer_id?: string;
    driver_id?: string;
    is_open?: boolean;
    period?: string;
    start_date?: string;
    end_date?: string;
    ordering?: string;
  }): Observable<TripListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<TripListResponse>(`${this.baseUrl}/trips/trips/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getTripsSummary(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    status?: string;
    is_open?: boolean;
    search?: string;
    driver_id?: string;
  }): Observable<TripsSummary> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<TripsSummary>(`${this.baseUrl}/trips/trips/summary/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getTrip(id: string): Observable<TripDetail> {
    return this.http.get<TripDetail>(`${this.baseUrl}/trips/trips/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createTrip(payload: CreateTripRequest): Observable<TripDetail> {
    return this.http.post<TripDetail>(`${this.baseUrl}/trips/trips/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateTripStatus(id: string, body: { to_status: string; notes?: string }): Observable<TripDetail> {
    return this.http.post<TripDetail>(`${this.baseUrl}/trips/trips/${id}/status/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getTripRoutes(params?: {
    is_active?: boolean;
    page_size?: number;
    page?: number;
    search?: string;
  }): Observable<TripRouteListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.http.get<TripRouteListResponse>(`${this.baseUrl}/trips/routes/${qs}`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getFuelOrder(id: string): Observable<FuelOrderDetail> {
    return this.http.get<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createFuelOrder(payload: CreateFuelOrderRequest): Observable<FuelOrderDetail> {
    return this.http.post<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateFuelOrder(id: string, payload: UpdateFuelOrderRequest): Observable<FuelOrderDetail> {
    return this.http.patch<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  completeFuelOrder(id: string, body: { invoice_number: string; invoice_date?: string; filled_at?: string }): Observable<FuelOrderDetail> {
    return this.http.post<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/${id}/complete/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  cancelFuelOrder(id: string, body?: { reason?: string }): Observable<FuelOrderDetail> {
    return this.http.post<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/${id}/cancel/`, body || {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  payFuelOrder(id: string, body: {
    amount: string;
    payment_method_id: string;
    invoice_number?: string;
    reference?: string;
    notes?: string;
    paid_at?: string;
  }): Observable<FuelOrderDetail> {
    return this.http.post<FuelOrderDetail>(`${this.baseUrl}/fuel-orders/${id}/pay/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** GET /api/fuel-orders/station-payables/ — outstanding balances grouped by station */
  getStationPayables(params?: { station_id?: string }): Observable<StationPayable[]> {
    return this.http.get<StationPayable[]>(
      `${this.baseUrl}/fuel-orders/station-payables/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /** POST /api/fuel-orders/bulk-pay/ — lump sum settled oldest-first at one station */
  bulkPayFuelOrders(body: BulkPayFuelOrdersRequest): Observable<BulkPayFuelOrdersResponse> {
    return this.http.post<BulkPayFuelOrdersResponse>(`${this.baseUrl}/fuel-orders/bulk-pay/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Trailers, couplings and fleet rigs ───────────────────────────────────

  getTrailerTypes(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<TrailerTypeListResponse> {
    return this.http.get<TrailerTypeListResponse>(
      `${this.baseUrl}/trailer_types/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getTrailers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    trailer_type?: string;
    status?: string;
    ordering?: string;
  }): Observable<TrailerListResponse> {
    return this.http.get<TrailerListResponse>(
      `${this.baseUrl}/trailers/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getTrailer(id: string): Observable<Trailer> {
    return this.http.get<Trailer>(`${this.baseUrl}/trailers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createTrailer(payload: CreateTrailerRequest): Observable<Trailer> {
    return this.http.post<Trailer>(`${this.baseUrl}/trailers/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateTrailer(id: string, payload: Partial<CreateTrailerRequest>): Observable<Trailer> {
    return this.http.patch<Trailer>(`${this.baseUrl}/trailers/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteTrailer(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/trailers/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/trailers/{id}/uncouple/ — end the active coupling from the trailer side */
  uncoupleTrailer(trailerId: string): Observable<UncoupleTrailerResponse> {
    return this.http.post<UncoupleTrailerResponse>(`${this.baseUrl}/trailers/${trailerId}/uncouple/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getTrailerCouplings(params?: {
    page?: number;
    page_size?: number;
    prime_mover?: string;
    trailer?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<TrailerCouplingListResponse> {
    return this.http.get<TrailerCouplingListResponse>(
      `${this.baseUrl}/trailer_couplings/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getFleetRigs(params?: {
    page?: number;
    page_size?: number;
    is_active?: boolean;
    driver?: string;
    ordering?: string;
  }): Observable<FleetRigListResponse> {
    return this.http.get<FleetRigListResponse>(
      `${this.baseUrl}/fleet-rigs/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /** POST /api/vehicles/{id}/assign-rig/ — driver and trailer in one call */
  assignVehicleRig(vehicleId: string, body: AssignRigRequest): Observable<AssignRigResponse> {
    return this.http.post<AssignRigResponse>(`${this.baseUrl}/vehicles/${vehicleId}/assign-rig/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/vehicles/{id}/release-rig/ — clears both driver and trailer */
  releaseVehicleRig(vehicleId: string): Observable<AssignRigResponse> {
    return this.http.post<AssignRigResponse>(`${this.baseUrl}/vehicles/${vehicleId}/release-rig/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/vehicles/{id}/couple-trailer/ — keeps the current driver */
  coupleTrailerToVehicle(vehicleId: string, trailerId: string, notes?: string): Observable<CoupleTrailerResponse> {
    const body: { trailer_id: string; notes?: string } = { trailer_id: trailerId };
    if (notes?.trim()) {
      body.notes = notes.trim();
    }
    return this.http.post<CoupleTrailerResponse>(`${this.baseUrl}/vehicles/${vehicleId}/couple-trailer/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/vehicles/{id}/uncouple-trailer/ — keeps the current driver */
  uncoupleTrailerFromVehicle(vehicleId: string): Observable<UncoupleTrailerResponse> {
    return this.http.post<UncoupleTrailerResponse>(`${this.baseUrl}/vehicles/${vehicleId}/uncouple-trailer/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Trip documents ───────────────────────────────────────────────────────

  getTripDocuments(params?: {
    trip_id?: string;
    document_type?: string;
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Observable<TripDocumentListResponse> {
    return this.http.get<TripDocumentListResponse>(
      `${this.baseUrl}/trips/documents/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  /** POST /api/trips/documents/ — multipart upload (field name must be `file`) */
  uploadTripDocument(payload: UploadTripDocumentRequest): Observable<TripDocument> {
    const formData = new FormData();
    formData.append('trip', payload.trip);
    formData.append('document_type', payload.document_type);
    formData.append('file', payload.file, payload.file.name);
    if (payload.label?.trim()) {
      formData.append('label', payload.label.trim());
    }
    if (payload.notes?.trim()) {
      formData.append('notes', payload.notes.trim());
    }

    return this.http.post<TripDocument>(`${this.baseUrl}/trips/documents/`, formData, {
      headers: this.getFormDataHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Payroll ──────────────────────────────────────────────────────────────

  private normalizeLoginActivityList(res: unknown): LoginActivity[] {
    const raw = Array.isArray(res)
      ? res
      : (res as { results?: unknown[]; data?: unknown[] } | null)?.results
        ?? (res as { data?: unknown[] } | null)?.data
        ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map((item, index) => this.normalizeLoginActivity(item, index));
  }

  private normalizeLoginActivity(raw: unknown, index: number): LoginActivity {
    const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    const statusRaw = String(row['status'] ?? row['result'] ?? '').toLowerCase();
    const success = row['success'] === true || statusRaw === 'success' || statusRaw === 'ok';
    const failed = row['success'] === false
      || ['failed', 'failure', 'error', 'denied'].includes(statusRaw);
    const userAgent = String(row['user_agent'] ?? row['userAgent'] ?? row['ua'] ?? '');
    const locationParts = [row['city'], row['region'], row['country']]
      .map((part) => String(part ?? '').trim())
      .filter(Boolean);
    const location = String(row['location'] ?? row['geo'] ?? locationParts.join(', ') ?? '').trim();
    const device = String(
      row['device'] ?? row['browser'] ?? row['client'] ?? this.parseDeviceFromUserAgent(userAgent)
    ).trim();
    const at = String(
      row['created_at'] ?? row['timestamp'] ?? row['logged_in_at'] ?? row['login_at'] ?? row['date'] ?? ''
    );

    return {
      id: String(row['id'] ?? index),
      at,
      ip: String(row['ip_address'] ?? row['ip'] ?? row['remote_addr'] ?? '').trim() || '—',
      device: device || '—',
      location: location || '—',
      status: success ? 'success' : failed ? 'failed' : 'unknown',
      method: String(row['method'] ?? row['login_method'] ?? row['auth_method'] ?? '').trim() || '—',
      user_agent: userAgent,
    };
  }

  private parseDeviceFromUserAgent(ua: string): string {
    if (!ua) return '';
    const browser = ua.includes('Edg/') ? 'Edge'
      : ua.includes('Chrome/') ? 'Chrome'
      : ua.includes('Firefox/') ? 'Firefox'
      : ua.includes('Safari/') ? 'Safari'
      : '';
    const os = ua.includes('Android') ? 'Android'
      : ua.includes('iPhone') || ua.includes('iPad') ? 'iOS'
      : ua.includes('Mac OS') ? 'macOS'
      : ua.includes('Windows') ? 'Windows'
      : ua.includes('Linux') ? 'Linux'
      : '';
    return [browser, os].filter(Boolean).join(' on ');
  }

  private buildQuery(params?: Record<string, any>): string {
    if (!params) return '';
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    const qs = queryParams.toString();
    return qs ? `?${qs}` : '';
  }

  getPayrollRuns(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status_code?: string;
    period_start_from?: string;
    period_start_to?: string;
    period_end_from?: string;
    period_end_to?: string;
    ordering?: string;
  }): Observable<PayrollRunListResponse> {
    return this.http.get<PayrollRunListResponse>(
      `${this.baseUrl}/payroll-runs/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollRun(id: string): Observable<PayrollRun> {
    return this.http.get<PayrollRun>(`${this.baseUrl}/payroll-runs/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createPayrollRun(payload: CreatePayrollRunRequest): Observable<PayrollRun> {
    return this.http.post<PayrollRun>(`${this.baseUrl}/payroll-runs/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deletePayrollRun(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payroll-runs/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getPayComponentTypes(params?: {
    category?: string;
    active_only?: boolean;
    assignable?: boolean;
    page_size?: number;
  }): Observable<PayComponentTypeListResponse | PayComponentType[]> {
    return this.http.get<PayComponentTypeListResponse | PayComponentType[]>(
      `${this.baseUrl}/pay-component-types/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  createPayComponentType(payload: Partial<PayComponentType>): Observable<PayComponentType> {
    return this.http.post<PayComponentType>(`${this.baseUrl}/pay-component-types/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updatePayComponentType(id: string, payload: Partial<PayComponentType>): Observable<PayComponentType> {
    return this.http.patch<PayComponentType>(`${this.baseUrl}/pay-component-types/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deletePayComponentType(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/pay-component-types/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  addPayrollRunDeduction(runId: string, payload: {
    user_id: string;
    component_type_id: string;
    amount: string;
    description?: string;
  }): Observable<PayrollRunDeduction> {
    return this.http.post<PayrollRunDeduction>(`${this.baseUrl}/payroll-runs/${runId}/run-deductions/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deletePayrollRunDeduction(runId: string, deductionId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payroll-runs/${runId}/run-deductions/${deductionId}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  generatePayrollRun(id: string, body?: { user_ids?: string[]; branch_id?: string }): Observable<PayrollGenerateResponse> {
    return this.http.post<PayrollGenerateResponse>(`${this.baseUrl}/payroll-runs/${id}/generate/`, body || {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approvePayrollRun(id: string): Observable<PayrollRun> {
    return this.http.post<PayrollRun>(`${this.baseUrl}/payroll-runs/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  markPayrollRunPaid(id: string): Observable<PayrollRun> {
    return this.http.post<PayrollRun>(`${this.baseUrl}/payroll-runs/${id}/mark_paid/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  resendPayrollRunPayslips(id: string): Observable<{ queued: boolean; payroll_run_id: string }> {
    return this.http.post<{ queued: boolean; payroll_run_id: string }>(
      `${this.baseUrl}/payroll-runs/${id}/resend-payslips/`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollRunPayslips(id: string, params?: { page?: number; page_size?: number; user_id?: string }): Observable<PayslipListResponse> {
    return this.http.get<PayslipListResponse>(
      `${this.baseUrl}/payroll-runs/${id}/payslips/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollRunAdjustments(id: string, params?: { status?: string; page?: number; page_size?: number }): Observable<PayrollAdjustmentListResponse> {
    return this.http.get<PayrollAdjustmentListResponse>(
      `${this.baseUrl}/payroll-runs/${id}/adjustments/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollRunDeductions(id: string, params?: { status?: string; page?: number; page_size?: number }): Observable<PayrollDeductionListResponse> {
    return this.http.get<PayrollDeductionListResponse>(
      `${this.baseUrl}/payroll-runs/${id}/deductions/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollDepartmentAnalysisReport(params?: {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<PayrollDepartmentAnalysisReport> {
    return this.http.get<PayrollDepartmentAnalysisReport>(
      `${this.baseUrl}/payroll-runs/department-analysis-report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollStatutoryReport(params: {
    kind: 'paye' | 'nssf' | 'shif' | 'housing_levy' | string;
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<PayrollStatutoryReport> {
    return this.http.get<PayrollStatutoryReport>(
      `${this.baseUrl}/payroll-runs/statutory-report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportPayrollMasterData(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/payroll-runs/${id}/master-data/`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  exportPayrollStatutoryReport(params: {
    kind: 'paye' | 'nssf' | 'shif' | 'housing_levy' | string;
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/payroll-runs/statutory-report-export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  exportPayrollDepartmentAnalysisReport(params?: {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/payroll-runs/department-analysis-report-export/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders(), responseType: 'blob' }
    ).pipe(catchError(this.handleError));
  }

  getPayrollPenaltyReport(params?: {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<PayrollStatutoryReport> {
    return this.http.get<PayrollStatutoryReport>(
      `${this.baseUrl}/payroll-runs/penalty-report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportPayrollPenaltyReport(params?: {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/payroll-runs/penalty-report-export/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders(), responseType: 'blob' }
    ).pipe(catchError(this.handleError));
  }

  getPayrollBankTransferReport(params?: ReportDateParams & {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<PayrollBankTransferReport> {
    return this.http.get<PayrollBankTransferReport>(
      `${this.baseUrl}/payroll-runs/bank-transfer-report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportPayrollBankTransferReport(params?: ReportDateParams & {
    payroll_run_id?: string;
    department_id?: string;
  }): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/payroll-runs/bank-transfer-report-export/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders(), responseType: 'blob' }
    ).pipe(catchError(this.handleError));
  }

  getStatutoryConfigs(): Observable<StatutoryConfigListResponse | StatutoryConfig[]> {
    return this.http.get<StatutoryConfigListResponse | StatutoryConfig[]>(
      `${this.baseUrl}/statutory-configs/${this.buildQuery({ page_size: 50 })}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getCurrentStatutoryConfig(asOf?: string): Observable<StatutoryConfig> {
    return this.http.get<StatutoryConfig>(
      `${this.baseUrl}/statutory-configs/current/${this.buildQuery(asOf ? { as_of: asOf } : undefined)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  saveCurrentStatutoryConfig(payload: StatutoryConfigWrite): Observable<StatutoryConfig> {
    return this.http.put<StatutoryConfig>(
      `${this.baseUrl}/statutory-configs/current/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollAdjustments(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    adjustment_type?: string;
    user_id?: string;
    payroll_run_id?: string;
    ordering?: string;
  }): Observable<PayrollAdjustmentListResponse> {
    return this.http.get<PayrollAdjustmentListResponse>(
      `${this.baseUrl}/payroll-adjustments/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayrollAdjustmentsSummary(params?: {
    adjustment_type?: string;
    status?: string;
    payroll_run_id?: string;
  }): Observable<PayrollAdjustmentSummaryResponse> {
    return this.http.get<PayrollAdjustmentSummaryResponse>(
      `${this.baseUrl}/payroll-adjustments/summary/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  createPayrollAdjustment(payload: CreatePayrollAdjustmentRequest): Observable<PayrollAdjustment> {
    return this.http.post<PayrollAdjustment>(`${this.baseUrl}/payroll-adjustments/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approvePayrollAdjustment(id: string): Observable<PayrollAdjustment> {
    return this.http.post<PayrollAdjustment>(`${this.baseUrl}/payroll-adjustments/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  rejectPayrollAdjustment(id: string, reason: string): Observable<PayrollAdjustment> {
    return this.http.post<PayrollAdjustment>(`${this.baseUrl}/payroll-adjustments/${id}/reject/`, { reason }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approvePayrollDeduction(id: string): Observable<PayrollDeduction> {
    return this.http.post<PayrollDeduction>(`${this.baseUrl}/payroll-deductions/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  rejectPayrollDeduction(id: string, reason: string): Observable<PayrollDeduction> {
    return this.http.post<PayrollDeduction>(`${this.baseUrl}/payroll-deductions/${id}/reject/`, { reason }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getStaffLoans(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    user_id?: string;
    has_balance?: boolean;
    ordering?: string;
  }): Observable<StaffLoanListResponse> {
    return this.http.get<StaffLoanListResponse>(
      `${this.baseUrl}/staff-loans/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getStaffLoan(id: string): Observable<StaffLoan> {
    return this.http.get<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createStaffLoan(payload: CreateStaffLoanRequest): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateStaffLoan(id: string, payload: Partial<CreateStaffLoanRequest>): Observable<StaffLoan> {
    return this.http.patch<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteStaffLoan(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/staff-loans/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approveStaffLoan(id: string): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  rejectStaffLoan(id: string, reason: string): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/reject/`, { reason }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  disburseStaffLoan(id: string, payload: {
    disbursement_method: string;
    disbursement_reference?: string;
    notes?: string;
  }): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/disburse/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  cancelStaffLoan(id: string, reason?: string): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/cancel/`, { reason: reason || '' }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  recordStaffLoanRepayment(id: string, payload: { amount: string | number; notes?: string }): Observable<StaffLoan> {
    return this.http.post<StaffLoan>(`${this.baseUrl}/staff-loans/${id}/record_repayment/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getStaffLoanSummary(params?: { user_id?: string; status?: string }): Observable<StaffLoanSummaryResponse> {
    return this.http.get<StaffLoanSummaryResponse>(
      `${this.baseUrl}/staff-loans/summary/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getUpcomingLoanDeductions(params: {
    period_end: string;
    user_ids?: string;
  }): Observable<UpcomingLoanDeductionResponse> {
    return this.http.get<UpcomingLoanDeductionResponse>(
      `${this.baseUrl}/staff-loans/upcoming/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getSalaryAdvances(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    user_id?: string;
    has_balance?: boolean;
    repayment_mode?: string;
    ordering?: string;
  }): Observable<SalaryAdvanceListResponse> {
    return this.http.get<SalaryAdvanceListResponse>(
      `${this.baseUrl}/salary-advances/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getSalaryAdvance(id: string): Observable<SalaryAdvance> {
    return this.http.get<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createSalaryAdvance(payload: CreateSalaryAdvanceRequest): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateSalaryAdvance(id: string, payload: Partial<CreateSalaryAdvanceRequest>): Observable<SalaryAdvance> {
    return this.http.patch<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteSalaryAdvance(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/salary-advances/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approveSalaryAdvance(id: string): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  rejectSalaryAdvance(id: string, reason: string): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/reject/`, { reason }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  disburseSalaryAdvance(id: string, payload: {
    disbursement_method: string;
    disbursement_reference?: string;
    notes?: string;
  }): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/disburse/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  cancelSalaryAdvance(id: string, reason?: string): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/cancel/`, { reason: reason || '' }, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  recordSalaryAdvanceRepayment(id: string, payload: { amount: string | number; notes?: string }): Observable<SalaryAdvance> {
    return this.http.post<SalaryAdvance>(`${this.baseUrl}/salary-advances/${id}/record_repayment/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getSalaryAdvanceSummary(params?: { user_id?: string; status?: string }): Observable<SalaryAdvanceSummaryResponse> {
    return this.http.get<SalaryAdvanceSummaryResponse>(
      `${this.baseUrl}/salary-advances/summary/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getUpcomingAdvanceDeductions(params: {
    period_end: string;
    user_ids?: string;
  }): Observable<UpcomingAdvanceDeductionResponse> {
    return this.http.get<UpcomingAdvanceDeductionResponse>(
      `${this.baseUrl}/salary-advances/upcoming/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPayslipPdf(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/payslips/${id}/pdf/`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  resendPayslipEmail(id: string): Observable<{ queued: boolean; payslip_id: string; recipient: string }> {
    return this.http.post<{ queued: boolean; payslip_id: string; recipient: string }>(
      `${this.baseUrl}/payslips/${id}/resend-email/`,
      {},
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // ─── Finance: expense categories ──────────────────────────────────────────

  getExpenseCategories(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    is_vehicle_expense?: boolean;
    ordering?: string;
  }): Observable<ExpenseCategoryListResponse> {
    return this.http.get<ExpenseCategoryListResponse>(
      `${this.baseUrl}/expense-categories/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getExpenseCategory(id: string): Observable<ExpenseCategory> {
    return this.http.get<ExpenseCategory>(`${this.baseUrl}/expense-categories/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createExpenseCategory(payload: CreateExpenseCategoryRequest): Observable<ExpenseCategory> {
    return this.http.post<ExpenseCategory>(`${this.baseUrl}/expense-categories/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateExpenseCategory(id: string, payload: Partial<CreateExpenseCategoryRequest>): Observable<ExpenseCategory> {
    return this.http.patch<ExpenseCategory>(`${this.baseUrl}/expense-categories/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Finance: petty cash funds ────────────────────────────────────────────

  getPettyCashFunds(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    branch_id?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<PettyCashFundListResponse> {
    return this.http.get<PettyCashFundListResponse>(
      `${this.baseUrl}/petty-cash-funds/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPettyCashFund(id: string): Observable<PettyCashFund> {
    return this.http.get<PettyCashFund>(`${this.baseUrl}/petty-cash-funds/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createPettyCashFund(payload: CreatePettyCashFundRequest): Observable<PettyCashFund> {
    return this.http.post<PettyCashFund>(`${this.baseUrl}/petty-cash-funds/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updatePettyCashFund(id: string, payload: Partial<CreatePettyCashFundRequest>): Observable<PettyCashFund> {
    return this.http.patch<PettyCashFund>(`${this.baseUrl}/petty-cash-funds/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  replenishPettyCashFund(id: string, payload: ReplenishPettyCashFundRequest): Observable<ReplenishPettyCashFundResponse> {
    return this.http.post<ReplenishPettyCashFundResponse>(
      `${this.baseUrl}/petty-cash-funds/${id}/replenish/`,
      payload,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getPettyCashFundLedger(id: string, params?: {
    year?: number;
    month?: number;
    period?: string;
    start_date?: string;
    end_date?: string;
    vehicle_id?: string;
    search?: string;
    min_amount?: string;
    max_amount?: string;
  }): Observable<PettyCashLedgerResponse> {
    return this.http.get<PettyCashLedgerResponse>(
      `${this.baseUrl}/petty-cash-funds/${id}/ledger/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportPettyCashFundLedger(id: string, params?: {
    year?: number;
    month?: number;
    period?: string;
    start_date?: string;
    end_date?: string;
    vehicle_id?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/petty-cash-funds/${id}/ledger/export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  // ─── Finance: petty cash transactions ─────────────────────────────────────

  getPettyCashTransactions(params?: {
    page?: number;
    page_size?: number;
    fund_id?: string;
    transaction_type?: PettyCashTransactionType;
    date_from?: string;
    date_to?: string;
    ordering?: string;
  }): Observable<PettyCashTransactionListResponse> {
    return this.http.get<PettyCashTransactionListResponse>(
      `${this.baseUrl}/petty-cash-transactions/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // ─── Finance: expenses ────────────────────────────────────────────────────

  getExpenses(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    vehicle_id?: string;
    category_id?: string;
    fuel_order_id?: string;
    status?: ExpenseStatus | string;
    status_in?: string;
    payment_source?: ExpensePaymentSource | string;
    petty_cash_fund_id?: string;
    date_from?: string;
    date_to?: string;
    ordering?: string;
  }): Observable<ExpenseListResponse> {
    return this.http.get<ExpenseListResponse>(
      `${this.baseUrl}/expenses/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getExpense(id: string): Observable<Expense> {
    return this.http.get<Expense>(`${this.baseUrl}/expenses/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createExpense(payload: CreateExpenseRequest): Observable<Expense> {
    return this.http.post<Expense>(`${this.baseUrl}/expenses/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateExpense(id: string, payload: Partial<CreateExpenseRequest>): Observable<Expense> {
    return this.http.patch<Expense>(`${this.baseUrl}/expenses/${id}/`, payload, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  submitExpense(id: string): Observable<Expense> {
    return this.http.post<Expense>(`${this.baseUrl}/expenses/${id}/submit/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  approveExpense(id: string): Observable<Expense> {
    return this.http.post<Expense>(`${this.baseUrl}/expenses/${id}/approve/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  payExpense(id: string): Observable<Expense> {
    return this.http.post<Expense>(`${this.baseUrl}/expenses/${id}/pay/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  getExpensesSummary(params?: {
    vehicle_id?: string;
    category_id?: string;
    status?: string;
    payment_source?: string;
    petty_cash_fund_id?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
  }): Observable<ExpensesSummary> {
    return this.http.get<ExpensesSummary>(
      `${this.baseUrl}/expenses/summary/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // ─── Finance: dashboard reports ───────────────────────────────────────────

  getFuelOrdersReport(params?: ReportDateParams & {
    station_id?: string;
    payment_status?: string;
    status?: string;
    status_code?: string;
    page?: number;
    page_size?: number;
  }): Observable<FuelOrdersDashboardReport> {
    return this.http.get<FuelOrdersDashboardReport>(
      `${this.baseUrl}/dashboard/fuel_orders_report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportDashboardFuelOrdersReport(params?: ReportDateParams & {
    station_id?: string;
    payment_status?: string;
    status?: string;
    status_code?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/dashboard/fuel_orders_report/export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  exportFuelOrdersReport(params?: ReportDateParams & {
    station_id?: string;
    payment_status?: string;
    status?: string;
    status_code?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/fuel-orders/station-orders/export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  getStationCreditReport(params?: ReportDateParams & {
    station_id?: string;
    payment_status?: string;
    status?: string;
    status_code?: string;
    page?: number;
    page_size?: number;
    payments_page?: number;
    payments_page_size?: number;
  }): Observable<StationCreditReport> {
    return this.http.get<StationCreditReport>(
      `${this.baseUrl}/fuel-orders/station-credit-account/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getStationFuelAccountReport(params?: ReportDateParams & {
    station_id?: string;
  }): Observable<StationFuelAccountReport> {
    return this.http.get<StationFuelAccountReport>(
      `${this.baseUrl}/fuel-orders/station-fuel-account/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportStationFuelAccount(params?: ReportDateParams & {
    station_id?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/fuel-orders/station-fuel-account/export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  getPettyCashReport(params?: ReportDateParams & {
    branch_id?: string;
    fund_id?: string;
    page?: number;
    page_size?: number;
  }): Observable<PettyCashDashboardReport> {
    return this.http.get<PettyCashDashboardReport>(
      `${this.baseUrl}/dashboard/petty_cash_report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  exportDashboardPettyCashReport(params?: ReportDateParams & {
    branch_id?: string;
    fund_id?: string;
  }): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/dashboard/petty_cash_report/export/${this.buildQuery(params)}`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  getExpensesReport(params?: ReportDateParams & {
    branch_id?: string;
    category_id?: string;
    vehicle_id?: string;
    status?: string;
    payment_source?: string;
    page?: number;
    page_size?: number;
  }): Observable<ExpensesDashboardReport> {
    return this.http.get<ExpensesDashboardReport>(
      `${this.baseUrl}/dashboard/expenses_report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  getVehicleExpensesReport(params?: ReportDateParams & {
    vehicle_id?: string;
    category_id?: string;
    status?: string;
    payment_source?: string;
    page?: number;
    page_size?: number;
  }): Observable<VehicleExpensesReport> {
    return this.http.get<VehicleExpensesReport>(
      `${this.baseUrl}/dashboard/vehicle_expenses_report/${this.buildQuery(params)}`,
      { headers: this.getAuthHeaders() }
    ).pipe(catchError(this.handleError));
  }

  // ─── Parking: configuration ───────────────────────────────────────────────

  /** Some parking list endpoints are unpaginated; collapse both shapes into one. */
  private parkingList<T>(url: string): Observable<ParkingListResponse<T>> {
    return this.http.get<any>(url, { headers: this.getAuthHeaders() }).pipe(
      map((res: any): ParkingListResponse<T> => {
        if (Array.isArray(res)) {
          return { count: res.length, next: null, previous: null, results: res as T[] };
        }
        const results: T[] = res?.results ?? [];
        return {
          count: res?.count ?? results.length,
          next: res?.next ?? null,
          previous: res?.previous ?? null,
          results,
        };
      }),
      catchError(this.handleError)
    );
  }

  /** GET /api/parking-configs/ — one config per company */
  getParkingConfigs(params?: {
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Observable<ParkingListResponse<ParkingConfig>> {
    return this.parkingList<ParkingConfig>(`${this.baseUrl}/parking-configs/${this.buildQuery(params)}`);
  }

  getParkingConfig(id: string): Observable<ParkingConfig> {
    return this.http.get<ParkingConfig>(`${this.baseUrl}/parking-configs/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createParkingConfig(body: ParkingConfigWrite): Observable<ParkingConfig> {
    return this.http.post<ParkingConfig>(`${this.baseUrl}/parking-configs/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateParkingConfig(id: string, body: Partial<ParkingConfigWrite>): Observable<ParkingConfig> {
    return this.http.patch<ParkingConfig>(`${this.baseUrl}/parking-configs/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Parking: yards ───────────────────────────────────────────────────────

  getParkingYards(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<ParkingListResponse<ParkingYard>> {
    return this.parkingList<ParkingYard>(`${this.baseUrl}/parking-yards/${this.buildQuery(params)}`);
  }

  getParkingYard(id: string): Observable<ParkingYard> {
    return this.http.get<ParkingYard>(`${this.baseUrl}/parking-yards/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createParkingYard(body: ParkingYardWrite): Observable<ParkingYard> {
    return this.http.post<ParkingYard>(`${this.baseUrl}/parking-yards/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateParkingYard(id: string, body: Partial<ParkingYardWrite>): Observable<ParkingYard> {
    return this.http.patch<ParkingYard>(`${this.baseUrl}/parking-yards/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteParkingYard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/parking-yards/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Parking: vehicle owners ──────────────────────────────────────────────

  getVehicleOwners(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<ParkingListResponse<VehicleOwner>> {
    return this.parkingList<VehicleOwner>(`${this.baseUrl}/vehicle-owners/${this.buildQuery(params)}`);
  }

  getVehicleOwner(id: string): Observable<VehicleOwner> {
    return this.http.get<VehicleOwner>(`${this.baseUrl}/vehicle-owners/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createVehicleOwner(body: VehicleOwnerWrite): Observable<VehicleOwner> {
    return this.http.post<VehicleOwner>(`${this.baseUrl}/vehicle-owners/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateVehicleOwner(id: string, body: Partial<VehicleOwnerWrite>): Observable<VehicleOwner> {
    return this.http.patch<VehicleOwner>(`${this.baseUrl}/vehicle-owners/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteVehicleOwner(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/vehicle-owners/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Parking: owner vehicles ──────────────────────────────────────────────

  getOwnerVehicles(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    owner_id?: string;
    registration_number?: string;
    is_active?: boolean;
    ordering?: string;
  }): Observable<ParkingListResponse<OwnerVehicle>> {
    return this.parkingList<OwnerVehicle>(`${this.baseUrl}/owner-vehicles/${this.buildQuery(params)}`);
  }

  getOwnerVehicle(id: string): Observable<OwnerVehicle> {
    return this.http.get<OwnerVehicle>(`${this.baseUrl}/owner-vehicles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  createOwnerVehicle(body: OwnerVehicleWrite): Observable<OwnerVehicle> {
    return this.http.post<OwnerVehicle>(`${this.baseUrl}/owner-vehicles/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  updateOwnerVehicle(id: string, body: Partial<OwnerVehicleWrite>): Observable<OwnerVehicle> {
    return this.http.patch<OwnerVehicle>(`${this.baseUrl}/owner-vehicles/${id}/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  deleteOwnerVehicle(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/owner-vehicles/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Parking: sessions (yard entry / exit) ────────────────────────────────

  getParkingSessions(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: ParkingSessionStatus | string;
    is_inside?: boolean;
    yard_id?: string;
    owner_id?: string;
    registration_number?: string;
    date_from?: string;
    date_to?: string;
    ordering?: string;
  }): Observable<ParkingListResponse<ParkingSession>> {
    return this.parkingList<ParkingSession>(`${this.baseUrl}/parking-sessions/${this.buildQuery(params)}`);
  }

  getParkingSession(id: string): Observable<ParkingSession> {
    return this.http.get<ParkingSession>(`${this.baseUrl}/parking-sessions/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-sessions/entry/ — record gate entry */
  recordParkingEntry(body: ParkingSessionEntryRequest): Observable<ParkingSession> {
    return this.http.post<ParkingSession>(`${this.baseUrl}/parking-sessions/entry/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-sessions/{id}/exit/ — record gate exit, fee is calculated */
  recordParkingExit(id: string, body?: ParkingSessionExitRequest): Observable<ParkingSession> {
    return this.http.post<ParkingSession>(`${this.baseUrl}/parking-sessions/${id}/exit/`, body || {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  // ─── Parking: monthly bills ───────────────────────────────────────────────

  getParkingBills(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    owner_id?: string;
    status?: ParkingBillStatus | string;
    payment_status?: string;
    period_start?: string;
    period_end?: string;
    ordering?: string;
  }): Observable<ParkingListResponse<ParkingBill>> {
    return this.parkingList<ParkingBill>(`${this.baseUrl}/parking-bills/${this.buildQuery(params)}`);
  }

  getParkingBill(id: string): Observable<ParkingBill> {
    return this.http.get<ParkingBill>(`${this.baseUrl}/parking-bills/${id}/`, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-bills/generate-monthly/ — one bill per owner with billable sessions */
  generateMonthlyParkingBills(body: GenerateParkingBillsRequest): Observable<GenerateParkingBillsResponse> {
    return this.http.post<GenerateParkingBillsResponse>(`${this.baseUrl}/parking-bills/generate-monthly/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-bills/generate-for-owner/ */
  generateParkingBillForOwner(body: GenerateParkingBillForOwnerRequest): Observable<ParkingBill> {
    return this.http.post<ParkingBill>(`${this.baseUrl}/parking-bills/generate-for-owner/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-bills/{id}/issue/ — move a draft bill to issued */
  issueParkingBill(id: string): Observable<ParkingBill> {
    return this.http.post<ParkingBill>(`${this.baseUrl}/parking-bills/${id}/issue/`, {}, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** POST /api/parking-bills/{id}/pay/ */
  payParkingBill(id: string, body: ParkingBillPayRequest): Observable<ParkingBill> {
    return this.http.post<ParkingBill>(`${this.baseUrl}/parking-bills/${id}/pay/`, body, {
      headers: this.getAuthHeaders()
    }).pipe(catchError(this.handleError));
  }

  /** GET /api/parking-bills/{id}/invoice/ — invoice PDF */
  getParkingBillInvoicePdf(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/parking-bills/${id}/invoice/`, {
      headers: this.getAuthHeaders(),
      responseType: 'blob'
    }).pipe(catchError(this.handleError));
  }

  getParkingBillPayments(params?: {
    page?: number;
    page_size?: number;
    bill?: string;
    ordering?: string;
  }): Observable<ParkingListResponse<ParkingBillPayment>> {
    return this.parkingList<ParkingBillPayment>(`${this.baseUrl}/parking-bill-payments/${this.buildQuery(params)}`);
  }
}

export interface ProductOfferProductRef {
  id: string;
  name: string;
  sku?: string;
  unit_of_measure_code?: string;
}

export interface ProductOffer {
  id: string;
  name: string;
  product: ProductOfferProductRef;
  product_id: string;
  quantity_required: string;
  period_months: number;
  reward_description: string;
  is_active: boolean;
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductOfferClaim {
  id: string;
  retailer: string;
  retailer_name: string;
  offer: string;
  offer_name: string;
  reward_description: string;
  product: string;
  product_id: string;
  product_name: string;
  quantity_achieved: string;
  period_start: string;
  period_end: string;
  qualified_at: string;
  status: 'qualified' | 'claimed' | 'fulfilled';
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductOfferProgressItem {
  offer: ProductOffer;
  quantity_required: string;
  quantity_achieved: string;
  period_months: number;
  qualified: boolean;
  claim: ProductOfferClaim | null;
}

